import React, { useEffect, useState, Suspense, lazy } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Collapse,
  InputNumber,
  DatePicker,
  Radio,
  Upload,
  Space,
  Typography,
  notification,
  Divider,
  Modal,
  Checkbox,
  Tooltip,
  Table,
  message,
  Spin,
  Grid,
} from "antd";
import {
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FileTextOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  MinusOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import ReactPhoneInput from "react-phone-input-2";
import infoPng from "../../../assets/icons/info.png";
import "react-phone-input-2/lib/style.css";

import "./employeeForm.css";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

// thunks / actions - adjust import path if needed
import {
  fetchAttendanceSummary,
  fetchClients,
  fetchClientUsers,
  fetchDepartments,
  fetchDesignations,
  fetchEmailSchedules,
  fetchEmployeeCode,
  fetchEmployeeMonthlyPayDetails,
  fetchEmployees,
  fetchEmployeeUsers,
  fetchMapEmployeeUsers,
  saveEmployee,
  saveEmployeeUser,
} from "../employeeThunks";

import {
  clearDraft,
  saveDraft,
} from "../../../store/commonSlices/uiDraftsSlice";
import { useRestoreDraft } from "../../../hooks/useRestoreDraft";
import { getInstitutionApi } from "../../../apiservice/setting";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchBranchList,
  fetchUserRoles,
} from "../../../store/commonThunks/lookups";
import {
  downloadEmployeeHistoryApi,
  downloadSalaryHistoryApi,
  hasEmployeeHistoryApi,
  hasSalaryHistoryApi,
  generateSalaryCertificateApi,
  getEmployeeLeaveSummaryApi,
} from "../../../apiservice/hrpayroll";
import EmployeeLeaveSummaryDrawer from "./EmployeeLeaveSummaryDrawer";
import UnmarkedAttendanceAlert from "./UnmarkedAttendanceAlert";
import GratuityEstimatorCard from "./GratuityEstimatorCard";
import EmployeeFormQuickNav from "./EmployeeFormQuickNav";
import { PanelHeader } from "./employeeFormPanelMeta";
import { getISOFromCountryName } from "../../../lib/currency";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import DepartmentFormModal from "../../Departments/components/DepartmentFormModal";
import DesignationFormModal from "../../Designations/components/DesignationFormModal";
import ClientFormModal from "../../Clients/components/ClientFormModal";
import ClientUserModal from "../../Clients/components/ClientUserModal";
import { loadAllNotifications } from "../../../store/commonThunks/notificationsThunk";
import { Trial_Limits } from "../../../lib/functions";
import { fetchSingleOnboarding, updateOnboardingStatus } from "../../Onboarding/onboardingThunks";
import Loading from "../../../components/Loading";

const { Panel } = Collapse;
const { TextArea } = Input;
const { Title, Text } = Typography;

const DRAFT_KEY = "employeeFormDraft";

// Presentational helpers for the "Leaves Information" panel's grouped cards.
const leaveCardStyle = (accent) => ({
  border: "1px solid #f0f0f0",
  borderTop: `3px solid ${accent}`,
  borderRadius: 8,
  padding: "14px 16px 6px",
  background: "#fff",
  height: "100%",
});

const LEAVE_CARD_HEADER_STYLE = {
  fontSize: 14,
  fontWeight: 700,
  color: "#262626",
  marginBottom: 8,
  paddingBottom: 8,
  borderBottom: "1px solid #f5f5f5",
};

const LeaveStatRow = ({ label, value, strong = false, noBorder = false }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 12,
      padding: "7px 0",
      borderBottom: noBorder ? "none" : "1px dashed #f0f0f0",
    }}
  >
    <span style={{ fontSize: 13, color: strong ? "#262626" : "#595959", fontWeight: strong ? 600 : 400 }}>
      {label}
    </span>
    <span style={{ fontSize: strong ? 15 : 13, fontWeight: strong ? 700 : 500, color: "#262626" }}>
      {value}
    </span>
  </div>
);

const EmployeeForm = ({
  editing = null,
  onboardId = null,
  onClose = () => { },
  authInfo,
  access_type,
  form: externalForm,
  mode = "full",
  onSuccess,
  setEditingRecord
}) => {
  const urlLocation = useLocation();
  const params = new URLSearchParams(urlLocation.search);

  const shouldRestore = params.get("restoreDraft") === "true";

  // lazy imports
  const PayslipPage = lazy(() => import("../components/EmployeePayslip/index"));
  const FinalSettlementForm = lazy(() =>
    import("../components/EmployeeFinalSettlement/index")
  );

  const [internalForm] = Form.useForm();
  const form = externalForm || internalForm;
  const dispatch = useDispatch();

  const {
    list: employees = [],
    employeeUsers = [],
    allbranchUsers = [],
    mapEmployeeUsers = [],
    departments = [],
    designations = [],
    clients = [],
    clientReportingManagers = [],
    emailSchedulesData = [],
    code = null,
    viewMode = false,
    saving = false,
    loading = false,
  } = useSelector((s) => s.employees || {});

  const { userRoles = [], branches = [] } = useSelector((s) => s.lookups);
  const { single: onboardingData } = useSelector((s) => s.onboarding);

  const navigate = useNavigate();

  const filteredUserRoles =
    authInfo?.trial_mode === "yes"
      ? userRoles.filter((role) => role.label === "employee")
      : userRoles;


  const [location, setLocation] = useState(null);

  const [fileList, setFileList] = useState([]); // for uploads
  const [companySettings, setCompanySettings] = useState(null);
  let [reporting_manager, reporting_manager_set] = useState(null);
  // Salary state and handlers
  const [employee_id, setEmployee_id] = useState(0);
  const [hideSalaryComponent, setHideSalaryComponent] = useState(false);
  const [hideSalaryDetails, setHideSalaryDetails] = useState(false);
  const [selectedAllowances, setSelectedAllowances] = useState([]);
  const [allowanceValues, setAllowanceValues] = useState([]);
  const [editingAllowance, setEditingAllowance] = useState(null);
  const [totalMonthlySalary, setTotalMonthlySalary] = useState(0);
  const [totalAnnualSalary, setTotalAnnualSalary] = useState(0);
  const [isEdit, setIsEdit] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  // --- Salary visibility and employee history
  const [hasEmployeeHistory, setHasEmployeeHistory] = useState(false);
  const [hasSalaryHistory, setHasSalaryHistory] = useState(false);
  const [salary_update_date, salary_update_date_set] = useState(null);
  const [target_amount, target_amount_set] = useState(0);
  const [commission_per, commission_per_set] = useState(0);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientReportingManager, setClientReportingManager] = useState(null);
  const [enable_attendance, enable_attendance_set] = useState(false);
  const [selected_day, selected_day_set] = useState(null);
  const [use_client_policy, use_client_policy_set] = useState(false);
  const [isAttendanceModalOpen, setIsAttendanceModalOpen] = useState(false);
  const [leavesTakenPrevYear, setLeavesTakenPrevYear] = useState(0);
  const [leavesTakenCurrYear, setLeavesTakenCurrYear] = useState(0);
  const [isEditingPrevYear, setIsEditingPrevYear] = useState(false);
  const [leaveSummary, setLeaveSummary] = useState(null);
  const [leaveSummaryLoading, setLeaveSummaryLoading] = useState(false);
  const [leaveSummaryDrawerOpen, setLeaveSummaryDrawerOpen] = useState(false);
  // Controlled accordion state so the quick-nav category chips can open a
  // panel and scroll to it, on top of the normal click-to-expand behavior.
  const [activeCollapseKey, setActiveCollapseKey] = useState(["personal"]);
  const openPanel = (key) => {
    setActiveCollapseKey([key]);
    requestAnimationFrame(() => {
      document.querySelector(`.emp-panel-anchor-${key}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  // Leaves Taken in Current Year is calculated live from approved/pending
  // attendance records (fixes "approved leaves not reflecting") instead of
  // relying on the manually-typed employees.leaves_taken_curr_year value.
  // leaves_taken_prev_year stays manual — it's the opening-balance field for
  // pre-ERP leave history, which no attendance record can ever reconstruct.
  useEffect(() => {
    if (!editing?.employee_id) return;
    let cancelled = false;
    setLeaveSummaryLoading(true);
    getEmployeeLeaveSummaryApi({ employee_id: editing.employee_id })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data;
        if (data) {
          setLeaveSummary(data);
          setLeavesTakenCurrYear(Number(data.annualLeave?.used || 0));
        }
      })
      .catch((err) => {
        console.error("Failed to load leave summary:", err);
      })
      .finally(() => {
        if (!cancelled) setLeaveSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editing?.employee_id]);
  const [bank_acc_number, bank_acc_number_set] = useState("");
  const [bank_acc_name, bank_acc_name_set] = useState("");
  const [bank_name, bank_name_set] = useState("");
  const [bank_branch, bank_branch_set] = useState("");
  const [iban_no, iban_no_set] = useState("");
  const [ifsc_code, ifsc_code_set] = useState("");
  const [pan_card, pan_card_set] = useState("");
  const [experiences, setExperiences] = useState([
    {
      employer: "",
      jobTitle: "",
      startDate: null,
      endDate: null,
      responsibilities: "",
      achievements: "",
    },
  ]);
  // const [performance_reviews, performance_reviews_set] = useState("");
  // const [goal_objectives, goal_objectives_set] = useState("");
  // const [feedback_improvements, feedback_improvements_set] = useState("");
  // const [training_development, training_development_set] = useState("");
  // const [promotion_career, promotion_career_set] = useState("");

  const [performance_reviews, performance_reviews_set] = useState([
    { date: null, review: "", goals: "", feedback: "", training: "", promotion: "" }
  ]);
  const [disciplinaryRecords, setDisciplinaryRecords] = useState([
    {
      date: null,
      disciplinaryActions: "",
      legalDocuments: "",
      contractDetails: "",
      actionTaken: "",
      acknowledged: false,
      documentFile: null,
    }
  ]);
  const [trainingData, setTrainingData] = useState({
    completedCourses: "",
    certifications: "",
    upcomingTrainings: "",
    skillsDevelopment: "",
    professionalDevelopment: "",
    trainingModes: [false, false, false], // [Online, In-person, Hybrid]
    futureTrainingInterest: false,
  });
  const [medical_records, medical_records_set] = useState("");
  const [workers_compensation, workers_compensation_set] = useState("");
  const [health_assessments, health_assessments_set] = useState("");
  const [health_plans, health_plans_set] = useState("");
  const [health_assessment_completed, set_health_assessment_completed] =
    useState("");
  const [first_aid_training, first_aid_training_set] = useState(false);
  const [newAsset, setNewAsset] = useState("");
  const [companyAssets, setCompanyAssets] = useState([]);
  const [signedPolicies, setSignedPolicies] = useState(false);
  const [confidentialityAgreements, setConfidentialityAgreements] =
    useState(false);
  const [linkedin, setLinkedin] = useState("");
  const [facebook, setFacebook] = useState("");
  const [workProjects, setWorkProjects] = useState("");
  const [reason_for_leaving, reason_for_leaving_set] = useState("");
  const [exit_interview, exit_interview_set] = useState("");
  const [final_settlement, final_settlement_set] = useState("");
  const [returnedAssets, setReturnedAssets] = useState({});
  const [isUserMapped, isUserMappedSet] = useState(false);
  const [is_map_login, is_map_login_set] = useState(false);
  const [is_login_modal, is_login_modal_set] = useState(false);
  const [is_email_sent, is_email_sent_set] = useState(false);
  const [user_id, user_id_set] = useState(0);
  const [selected_user, selected_user_set] = useState(null);
  const [user_map_modal, user_map_modal_set] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [formValues, formSetValues] = useState({
    selectedBranch: null,
    selectedUserRole: null,
  });
  const [mapped_user, mapped_user_set] = useState(false);
  const [emergency_country_code, emergency_country_code_set] = useState(+1);
  const [departmentModalOpen, setDepartmentModalOpen] = useState(false);
  const [designationModalOpen, setDesignationModalOpen] = useState(false);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [clientUserModalOpen, setClientUserModalOpen] = useState(false);
  const [contractCompletionDate, setContractCompletionDate] = useState(
    editing?.contract_completion_date
      ? dayjs(editing.contract_completion_date)
      : dayjs()
  );
  const [employeeCarryForwardOverride, setEmployeeCarryForwardOverride] = useState(
    editing?.employee_max_carry_forward ?? null
  );
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAdminAndHrUser, isAdminAndHrUserSet] = useState(false);
  const [isContractCompleted, setIsContractCompleted] = useState(false);
  // Per-employee weekend override — independent of use_client_policy (that's
  // about leave quotas, this is about which days off apply for this person).
  const [useCustomWeekend, setUseCustomWeekend] = useState(!!editing?.use_custom_weekend);
  const [customWeekendDays, setCustomWeekendDays] = useState(
    editing?.custom_weekend_days
      ? String(editing.custom_weekend_days).split(",").map(Number)
      : []
  );


  const defaultCountry = getISOFromCountryName(authInfo?.country);

  const isQuick = mode === "quick";

  const screens = Grid.useBreakpoint();

  const isFieldHidden = (fieldKey, pageKey = "employee_entry") => {
    return authInfo?.visibilitytabs?.some(
      (item) => item.page_key === pageKey && item.field_key === fieldKey
    );
  };


  const employeeCompletedOneYear = form.getFieldValue("joining_date") &&
    dayjs().diff(dayjs(form.getFieldValue("joining_date")), "year") >= 1;

  const annualLeaveDays = use_client_policy
    ? Number(selectedClient?.annual_leave_days || 0)
    : Number(companySettings?.annual_leave_days || 0);

  const prevYearRemaining =
    Math.max(annualLeaveDays - Number(leavesTakenPrevYear || 0), 0);

  const policyCarryForward = use_client_policy
    ? Number(selectedClient?.leaves_carry_forward || 0)
    : Number(companySettings?.leaves_carry_forward || 0);

  // employee override
  const employeeCarryForwardLimit = employeeCarryForwardOverride;
  // editing?.employee_max_carry_forward ?? null;

  const finalCarryForwardLimit =
    employeeCarryForwardLimit !== null
      ? Number(employeeCarryForwardLimit)
      : policyCarryForward;

  const carryForward = employeeCompletedOneYear
    ? Math.min(
      finalCarryForwardLimit,
      prevYearRemaining
    )
    : 0;


  // const takenLeaves = employeeCompletedOneYear
  //   ? Number(leavesTakenPrevYear || 0) + Number(leavesTakenCurrYear || 0)
  //   : Number(leavesTakenCurrYear || 0);

  const totalLeaveBalance =
    (employeeCompletedOneYear
      ? annualLeaveDays + carryForward
      : annualLeaveDays
    ) - Number(leavesTakenCurrYear || 0);



  const hide_salary_details_from_others_set = (checked) => {
    // store this in your form state or payload object
    setHideSalaryDetails(checked);
    // If you maintain this in redux/employee state, sync it there too:
    // dispatch(updateEmployeeField({ key: 'hide_salary_details', value: checked ? 1 : 0 }))
  };

  // DOWNLOAD SALARY CERTIFICATE
  const downloadSalaryCertificate = async () => {
    try {
      if (!Array.isArray(allowanceValues) || !editing) {
        message.warning("No employee or salary data found.");
        return;
      }

      const basicSalaryObj = allowanceValues?.find(
        (item) => item.name === "Basic_Salary"
      );
      const basicSalary = basicSalaryObj ? basicSalaryObj.amount || 0 : 0;
      const otherAllowance = (totalMonthlySalary || 0) - basicSalary;

      const employeeData = {
        employee_name: editing.employee_name,
        passport_no: editing.passport_no || "N/A",
        designation: editing.designation_name || "N/A",
        joining_date: editing.joining_date
          ? dayjs(editing.joining_date).format("YYYY-MM-DD")
          : "",
        basic_salary: basicSalary,
        other_allowance: otherAllowance,
        total_monthly_salary: totalMonthlySalary || 0,
      };

      message.loading("Generating salary certificate...", 0);

      const response = await generateSalaryCertificateApi(employeeData);

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "salary-certificate.docx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.destroy();
      message.success("Salary certificate downloaded successfully.");
    } catch (err) {
      console.error("Failed to generate salary certificate:", err);
      message.destroy();
      message.error("Failed to download salary certificate. Please try again.");
    }
  };

  /// to download history data
  const downloadExcel = async (type, employeeId) => {
    try {
      const apiFunc =
        type === "employee"
          ? downloadEmployeeHistoryApi // returns ZIP
          : downloadSalaryHistoryApi; // returns XLSX

      const response = await apiFunc(employeeId);

      let blob;
      let filename;

      if (type === "employee") {
        // ZIP file
        blob = new Blob([response.data], { type: "application/zip" });
        filename = `employee-history-${employeeId}.zip`;
      } else {
        // Excel file
        blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        filename = `salary-history-${employeeId}.xlsx`;
      }

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed:", err);
      message.error("Download failed. Please try again.");
    }
  };

  const isEmployeeTrialLimitReached =
    authInfo?.trial_mode == "yes" &&
    !editing &&
    employees.length >= Trial_Limits.employee_count;

  console.log(isEmployeeTrialLimitReached, employees.length, editing, "isEmployeeTrialLimitReached");

  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const res = await getInstitutionApi();
        setCompanySettings(res.data);
      } catch (err) {
        console.error("Error fetching company settings", err);
      }
    };
    fetchCompanySettings();
  }, []);

  useEffect(() => {
    dispatch(fetchDepartments());
    dispatch(fetchDesignations());
    dispatch(fetchMapEmployeeUsers());
    dispatch(fetchBranchList());
    dispatch(fetchUserRoles());
    dispatch(fetchEmployeeCode());
    // Skip heavy APIs in quick mode
    if (!isQuick) {
      dispatch(fetchClients());
      dispatch(fetchClientUsers());
      dispatch(fetchEmployees());
      dispatch(fetchEmailSchedules());
      // dispatch(fetchEmployeeUsers());
    }
  }, [dispatch]);

  useEffect(() => {
    if (!code) {
      dispatch(fetchEmployeeCode());
    }
  }, [code])


  useEffect(() => {
    const hrRoles = [
      // HR role
      "ForthERP_Basic_hr",
      "ForthERP_Standard_hr",
      "ForthERP_Pro_hr",
      "PharmaERP_Basic_hr",
      "PharmaERP_Standard_hr",
      "PharmaERP_Pro_hr",
      "PharmaBooks_Basic_hr",
      "PharmaBooks_Standard_hr",
      "PharmaBooks_Pro_hr",
      "ForthBooks_Basic_hr",
      "ForthBooks_Standard_hr",
      "ForthBooks_Pro_hr",
      "HRPayroll_Standard_hr",
      "HRPayroll_Pro_hr",
      "SuperERP_hr",
      'ForthCRM_Pro_hr',
      'ForthCRM_Standard_hr',

      // Admin role
      "ForthERP_Basic_admin",
      "ForthERP_Standard_admin",
      "ForthERP_Pro_admin",
      "PharmaERP_Basic_admin",
      "PharmaERP_Standard_admin",
      "PharmaERP_Pro_admin",
      "PharmaBooks_Basic_admin",
      "PharmaBooks_Standard_admin",
      "PharmaBooks_Pro_admin",
      "ForthBooks_Basic_admin",
      "ForthBooks_Standard_admin",
      "ForthBooks_Pro_admin",
      "HRPayroll_Standard_admin",
      "HRPayroll_Pro_admin",
      "SuperERP_admin",
      "ForthCRM_Standard_admin",
      "ForthCRM_Pro_admin",
    ];

    const userRoles = authInfo.userInfo.user_role_names
      ? authInfo.userInfo.user_role_names.split(",").map((role) => role.trim())
      : [];

    const isAdminHR = userRoles.some((role) => hrRoles.includes(role));

    isAdminAndHrUserSet(isAdminHR);
  }, [authInfo, authInfo.userInfo.user_role_names]);


  useEffect(() => {
    if (onboardId) {
      dispatch(fetchSingleOnboarding(onboardId));
    }
  }, [onboardId, dispatch]);

  useEffect(() => {
    if (!editing && !code) {
      dispatch(fetchEmployeeCode());
    }
  }, [dispatch, editing, code]);


  useEffect(() => {
    if (editing) {
      target_amount_set(editing.target_amount || 0);
      commission_per_set(editing.commission_per || 0);

      // Normalize mixed types before setting
      const normalizedCompanyAssets = Array.isArray(editing.company_assets)
        ? editing.company_assets
        : typeof editing.company_assets === "string"
          ? editing.company_assets.split(",").map((a) => a.trim())
          : [];

      const normalizedReturnedAssets =
        typeof editing.returned_assets === "string"
          ? JSON.parse(editing.returned_assets || "{}")
          : editing.returned_assets || {};

      const normalizedAllowances =
        typeof editing.selected_allowances === "string"
          ? JSON.parse(editing.selected_allowances || "[]")
          : editing.selected_allowances || [];

      let normalizedAllowanceValues = [];
      try {
        const raw =
          typeof editing.allowance_values === "string"
            ? JSON.parse(editing.allowance_values || "[]")
            : editing.allowance_values;

        // force it to be an array
        normalizedAllowanceValues = Array.isArray(raw) ? raw : [];
      } catch (e) {
        normalizedAllowanceValues = [];
      }

      // Restore allowances from DB
      const formattedAllowances = normalizedAllowances.map((a) =>
        a.replace(/_/g, " ")
      );

      const formattedValues = normalizedAllowanceValues.map((v) => ({
        // keep original key with underscores for matching logic
        name: v.name,
        amount: Number(v.amount) || 0,
      }));

      setSelectedAllowances(formattedAllowances); // "Basic Salary"
      setAllowanceValues(formattedValues);

      // Restore totals (same as old UI)
      setTotalMonthlySalary(editing?.total_monthly_salary);
      setTotalAnnualSalary(editing?.total_annual_salary);

      setEmployeeCarryForwardOverride(editing?.employee_max_carry_forward ?? null);
      setIsContractCompleted(!!editing.is_contract_completed);
      setUseCustomWeekend(!!editing?.use_custom_weekend);
      setCustomWeekendDays(
        editing?.custom_weekend_days
          ? String(editing.custom_weekend_days).split(",").map(Number)
          : []
      );

      // Recalculate so UI matches
      recalculateTotals(formattedValues);


      // map server values to form-friendly shapes
      const mapped = {
        ...editing,
        employee_code: editing.employee_code,
        company_employee_id: editing.company_employee_id,
        full_name: editing.employee_name,
        gender: editing.gender_type === "fmale" ? "female" : editing.gender_type,
        marital_status: editing.marital_type,
        dob: editing.birth_date ? dayjs(editing.birth_date) : null,
        email: editing.email_address,
        zip_code: editing.zip_code,
        present_address: editing.present_address,
        permanent_address: editing.permanent_address,
        country_code: editing.country_code,
        phone_no: editing.contact_no,
        father_name: editing.father_name,
        mother_name: editing.mother_name,
        emergency_name: editing.emergency_name,
        emergency_relation: editing.emergency_relation,
        emergency_country: editing.emergency_country_code,
        emergency_phone: editing.emergency_contact_no,
        national_id_no: editing.national_id_no,
        eid_no: editing.national_id_no,
        eid_expiry: editing.national_id_expiry
          ? dayjs(editing.national_id_expiry)
          : null,
        passport_no: editing.passport_no,
        passport_expiry: editing.passport_expiry
          ? dayjs(editing.passport_expiry)
          : null,
        visa_no: editing.visa_no,
        visa_expiry: editing.visa_expiry ? dayjs(editing.visa_expiry) : null,
        visa_issued_different_branch: !!editing.visa_issued_different_branch,
        visa_issue_branch_id: editing.visa_issue_branch_id,
        is_different_working_company: !!editing.is_different_working_company,
        working_company_name: editing.working_company_name,
        labour_contract_id: editing.labour_contract_id,
        labour_contract_expiry: editing.labour_contract_expiry
          ? dayjs(editing.labour_contract_expiry)
          : null,
        labour_card_id: editing.labour_card_id,
        labour_card_expiry: editing.labour_card_expiry
          ? dayjs(editing.labour_card_expiry)
          : null,
        insurance_name: editing.insurance_name,
        insurance_status: editing.insurance_status,
        insurance_expiry: editing.insurance_expiry
          ? dayjs(editing.insurance_expiry)
          : null,
        client_labour_contract_expiry: editing.client_labour_contract_expiry
          ? dayjs(editing.client_labour_contract_expiry)
          : null,
        first_reminder: editing.first_reminder,
        second_reminder: editing.second_reminder,
        joining_date: editing.joining_date ? dayjs(editing.joining_date) : null,
        department_id: editing.department_id
          ? String(editing.department_id)
          : undefined,
        designation_id: editing.designation_id
          ? String(editing.designation_id)
          : undefined,
        reporting_manager: editing.reporting_manager
          ? String(editing.reporting_manager)
          : undefined,
        employment_type: editing.employment_type,
        work_location: editing.work_location,
        employee_status: editing.employee_status,
        employee_type: editing.employeeType,
        job_grade: editing.job_grade,
        responsibilities: editing.responsibilities,
        salary_update_date: editing.salary_update_date
          ? dayjs(editing.salary_update_date)
          : null,
        contractCompletionDate: editing.contract_completion_date
          ? editing.contract_completion_date : null,
        target_amount: editing.target_amount,
        commission_per: editing.commission_per,
        bank_acc_number: editing.bank_acc_number,
        bank_acc_name: editing.bank_acc_name,
        bank_name: editing.bank_name,
        bank_branch: editing.bank_branch,
        iban_no: editing.iban_no,
        ifsc_code: editing.ifsc_code,
        pan_card: editing.pan_card,
        allowances: normalizedAllowanceValues,
      };

      // ✅ update form values
      form.setFieldsValue(mapped);
      setLocation(editing.client_location || null);

      // ✅ file uploads
      const mapFile = (url) =>
        url
          ? [
            {
              uid: url,
              name: url.split("/").pop(),
              status: "done",
              url,
            },
          ]
          : [];

      form.setFieldsValue({
        employee_photo: mapFile(editing.employee_photo_path),
        eid_scan: mapFile(editing.e_id_scan_path),
        passport_scan: mapFile(editing.passport_scan_path),
        visa_scan: mapFile(editing.visa_scan_path),
        labour_contract_scan: mapFile(editing.labour_contract_scan_path),
        labour_card_scan: mapFile(editing.labour_card_scan_path),
        insurance_scan: mapFile(editing.insurance_scan_path),
        client_labour_contract_scan: mapFile(
          editing.client_labour_contract_scan_path
        ),
        labour_cancellation_copy: mapFile(editing.labour_cancellation_copy_path),
        visa_cancellation_copy: mapFile(editing.visa_cancellation_copy_path),
        experience_letter: mapFile(editing.experience_letter_path),
        insurance_cancellation_copy: mapFile(editing.insurance_cancellation_copy_path),
        insurance_cancelled_refund: mapFile(editing.insurance_cancelled_refund_path),
        signed_final_settlement_copy: mapFile(editing.signed_final_settlement_copy_path),
        offer_letter_signed_scan: mapFile(editing.offer_signed_file_path),
        warning_letter: mapFile(editing.warning_letter_path),
        memo_document: mapFile(editing.memo_document_path),
        other_agreements_document: mapFile(editing.other_agreements_document_path),
      });

      // other information documents
      const existingOtherDocuments = (() => {
        if (!editing?.other_documents) return [];

        let raw = editing.other_documents;

        // Backend normally returns comma-separated URLs
        if (typeof raw !== "string") return [];

        raw = raw.trim();

        // Remove accidental markdown-link wrapper if present:
        // [url1,url2](url1,url2)
        if (raw.startsWith("[") && raw.includes("](")) {
          raw = raw.substring(1, raw.indexOf("]("));
        }

        return raw
          .split(",")
          .map((url) => url.trim())
          .filter(Boolean)
          .map((url, index) => ({
            uid: `existing-other-${index}`,
            name: decodeURIComponent(url.split("/").pop() || `Document ${index + 1}`),
            status: "done",
            url,
          }));
      })();

      console.log("Existing Other Documents:", existingOtherDocuments);

      form.setFieldsValue({
        other_documents: existingOtherDocuments,
      });

      const savedReviews = (() => {
        try {
          const raw = typeof editing.performance_reviews === "string"
            ? JSON.parse(editing.performance_reviews)
            : editing.performance_reviews;
          if (Array.isArray(raw) && raw.length > 0) return raw;
          // Legacy plain-text migration into first entry
          return [{
            date: null,
            review: editing.performance_reviews || "",
            goals: editing.goal_objectives || "",
            feedback: editing.feedback_improvements || "",
            training: editing.training_development || "",
            promotion: editing.promotion_career || "",
          }];
        } catch {
          return [{
            date: null,
            review: editing.performance_reviews || "",
            goals: editing.goal_objectives || "",
            feedback: editing.feedback_improvements || "",
            training: editing.training_development || "",
            promotion: editing.promotion_career || "",
          }];
        }
      })();
      performance_reviews_set(savedReviews);
      // ✅ Controlled states
      setEmployee_id(editing.employee_id || 0);
      emergency_country_code_set(editing.emergency_country_code);
      bank_acc_number_set(editing.bank_acc_number || "");
      bank_acc_name_set(editing.bank_acc_name || "");
      bank_name_set(editing.bank_name || "");
      bank_branch_set(editing.bank_branch || "");
      iban_no_set(editing.iban_no || "");
      ifsc_code_set(editing.ifsc_code || "");
      pan_card_set(editing.pan_card || "");
      // performance_reviews_set(editing.performance_reviews || "");
      // goal_objectives_set(editing.goal_objectives || "");
      // feedback_improvements_set(editing.feedback_improvements || "");
      // training_development_set(editing.training_development || "");
      // promotion_career_set(editing.promotion_career || "");
      salary_update_date_set(editing.salary_update_date || "");
      // setSelectedAllowances(normalizedAllowances);
      // setAllowanceValues(normalizedAllowanceValues);
      setSelectedClient(
        editing.client_id
          ? clients.find((c) => c.id === editing.client_id)
          : null
      );
      // setClientReportingManager(
      //   clientReportingManagers?.find(u => u.id === editing.client_id) || null
      // );
      enable_attendance_set(!!editing.scheduleAttendanceData);

      setTotalMonthlySalary(editing?.total_monthly_salary);
      recalculateTotals(formattedValues);

      setLeavesTakenPrevYear(editing?.leaves_taken_prev_year);
      setLeavesTakenCurrYear(editing?.leaves_taken_curr_year);
      target_amount_set(editing?.target_amount);
      commission_per_set(editing?.commission_per);

      setLinkedin(editing.linkedin || "");
      setFacebook(editing.facebook || "");
      setWorkProjects(editing.work_projects || "");
      setCompanyAssets(normalizedCompanyAssets);
      setReturnedAssets(normalizedReturnedAssets);
      reason_for_leaving_set(editing.reason_for_leaving || "");
      exit_interview_set(editing.exit_interview || "");
      final_settlement_set(editing.final_settlement || "");
      setContractCompletionDate(editing.contract_completion_date ? dayjs(editing.contract_completion_date) : dayjs())

      // Work experience
      setExperiences(
        Array.isArray(editing?.experiences) && editing?.experiences.length > 0
          ? editing?.experiences
          : [
            {
              employer: "",
              jobTitle: "",
              startDate: "",
              endDate: "",
              responsibilities: "",
              achievements: "",
            },
          ]
      );

      // ✅ Training data normalization
      setTrainingData({
        completedCourses: editing.completed_courses || "",
        certifications: editing.certifications || "",
        upcomingTrainings: editing.upcoming_trainings || "",
        skillsDevelopment: editing.skills_development || "",
        professionalDevelopment: editing.professional_development || "",
        trainingModes: [
          !!editing.training_mode_online,
          !!editing.training_mode_in_person,
          !!editing.training_mode_hybrid,
        ],
        futureTrainingInterest: !!editing.future_training_interest,
      });

      // ✅ Health & Safety
      medical_records_set(editing.medical_records || "");
      workers_compensation_set(editing.workers_compensation || "");
      health_assessments_set(editing.health_assessments || "");
      health_plans_set(editing.health_plans || "");
      set_health_assessment_completed(
        editing.health_assessment_completed ? "yes" : "no"
      );
      first_aid_training_set(!!editing.first_aid_training);

      const savedDisciplinary = (() => {
        try {
          const raw = typeof editing.disciplinary_records === "string"
            ? JSON.parse(editing.disciplinary_records)
            : editing.disciplinary_records;
          if (Array.isArray(raw) && raw.length > 0) return raw;
          // Legacy migration
          return [{
            date: null,
            disciplinaryActions: editing.disciplinary_actions || "",
            legalDocuments: editing.legal_documents || "",
            contractDetails: editing.contract_details || "",
            actionTaken: editing.action_taken || "",
            acknowledged: !!editing.acknowledged,
            documentFile: null,
          }];
        } catch {
          return [{
            date: null,
            disciplinaryActions: editing.disciplinary_actions || "",
            legalDocuments: editing.legal_documents || "",
            contractDetails: editing.contract_details || "",
            actionTaken: editing.action_taken || "",
            acknowledged: !!editing.acknowledged,
            documentFile: null,
          }];
        }
      })();
      setDisciplinaryRecords(savedDisciplinary);
      setReturnedAssets(
        editing?.returned_assets ? editing?.returned_assets : {}
      );
      setSignedPolicies(editing?.signed_policies);
      setConfidentialityAgreements(editing?.confidentiality_agreements);

      // **Check if employee has a client-specific leave policy **
      const emailSchedule = emailSchedulesData?.find(
        (e) => e.employee_id == editing.employee_id
      );

      if (emailSchedule) {
        if (emailSchedule?.leave_policy_of_client_id) {
          const clientPolicy = clients.find(
            (c) => c.id == emailSchedule.leave_policy_of_client_id
          );
          setSelectedClient(clientPolicy || null);
          use_client_policy_set(!!clientPolicy);
        } else {
          const clientObject = clients?.find(
            (c) => c.name === editing?.client_name
          );
          setSelectedClient(clientObject || null);
        }

        // Set client reporting manager
        if (emailSchedule?.client_user_id) {
          const reportingManager = clientReportingManagers?.find(
            (manager) => manager.id == emailSchedule?.client_user_id
          );
          setClientReportingManager(reportingManager ? reportingManager : null);
        }

        // Enable attendance approval
        enable_attendance_set(true);
        selected_day_set(emailSchedule?.report_day);
      } else {
        // If no schedule is found
        const clientObject = clients?.find(
          (c) => c.name === editing?.client_name
        );
        setSelectedClient(clientObject || null);
        const reportingManager = clientReportingManagers
          ?.filter((manager) => manager.client_id == clientObject?.id)
          ?.find(
            (manager) =>
              manager.id == editing?.client_reporting_manager
          );
        setClientReportingManager(reportingManager ? reportingManager : null);
        use_client_policy_set(editing?.use_client_policy);
        enable_attendance_set(false);
        selected_day_set(null);
      }

      if (editing?.hide_salary_details === 1) {
        const accountantRoles = [
          "ForthERP_admin",
          "PharmaERP_admin",
          "PharmaBooks_admin",
          "ForthBooks_admin",
          "HRPayroll_admin",
          "ForthERP_accountant",
          "PharmaERP_accountant",
          "PharmaBooks_accountant",
          "ForthBooks_accountant",
        ];

        const userRoles = authInfo?.userInfo?.user_role_names
          ? authInfo.userInfo.user_role_names
            .split(",")
            .map((role) => role.trim())
          : [];

        const isAdmin = userRoles.some((role) =>
          accountantRoles.includes(role)
        );

        // Hide salary component for non-admins
        if (!isAdmin) setHideSalaryComponent(true);
        else setHideSalaryComponent(false);
      } else {
        setHideSalaryComponent(false);
      }

      const isMappedUserInUsers = allbranchUsers?.some((user) => {
        return (
          user.employee_id != null && // Ensure API employee_id is not null
          user.employee_id === editing.employee_id &&
          String(user.email).trim().toLowerCase() ===
          String(editing.email_address).trim().toLowerCase()
        );
      });

      isUserMappedSet(isMappedUserInUsers);
    } else if (code) {
      resetForm();
      form.setFieldsValue({
        employee_code: code,
        phone: {
          country_code: authInfo?.country_code || "971",
          contact_no: "",
        },
      });
      setFileList([]);
    }
  }, [editing, code, form, authInfo, clients, clientReportingManagers]);

  useEffect(() => {
    if (!employee_id) return;

    hasEmployeeHistoryApi(employee_id)
      .then((res) => setHasEmployeeHistory(res.data.hasData))
      .catch(() => setHasEmployeeHistory(false));

    hasSalaryHistoryApi(employee_id)
      .then((res) => setHasSalaryHistory(res.data.hasData))
      .catch(() => setHasSalaryHistory(false));
  }, [employee_id]);


  // conversion from onboarding 
  useEffect(() => {
    if (onboardId && onboardingData) {
      const data = onboardingData;
      form.setFieldsValue({
        full_name: data.full_name,
        email: data.email,
        country_code: data.country_code,
        phone_no: data.phone_no,

        emergency_country: data.secondary_country_code,
        emergency_phone: data.secondary_phone_no,
        gender: data.gender_type,
        marital_status: data.marital_type,
        department_id: String(data.department_id),
        designation_id: String(data.designation_id),
        present_address: data.present_address,
        permanent_address: data.permanent_address,
        reporting_manager: data.reporting_manager,
        employment_type: data.employment_type,
        work_location: data.work_location,
        job_grade: data.job_grade,
        joining_date: data.joining_date
          ? dayjs(data.joining_date)
          : null,
        offer_letter_signed_scan: data.offer_signed_file
          ? [
            {
              uid: data.offer_signed_file,
              name: data.offer_signed_file.split("/").pop(),
              status: "done",
              url: data.offer_signed_file,
            },
          ]
          : [],
      });

      // ✅ salary mapping (your fixed version)
      const normalizedAllowances = data.selected_allowances || [];
      const normalizedValues = data.allowance_values || [];

      const fullAllowanceValues = [
        {
          name: "Basic_Salary",
          amount: Number(data.basic_salary) || 0,
        },
        ...normalizedValues,
      ];

      const fullSelectedAllowances = [
        "Basic Salary",
        ...normalizedAllowances.map((a) =>
          a.replace(/_/g, " ")
        ),
      ];

      setSelectedAllowances(fullSelectedAllowances);
      setAllowanceValues(fullAllowanceValues);
      recalculateTotals(fullAllowanceValues);

      setIsInitialized(true);

    }
  }, [departments, designations, isInitialized, onboardingData]);

  const onPhoneChange = (value, country) => {
    // value may sometimes be object in weird wrappers; ensure string
    const val = typeof value === "string" ? value : String(value ?? "");
    const dialCode = country?.dialCode || "";
    const phoneNumber = val
      .replace(`+${dialCode}`, "")
      .replace(/[^0-9]/g, "")
      .trim();
    form.setFieldValue("phone", {
      country_code: dialCode,
      contact_no: phoneNumber,
    });
  };

  const onLocationChange = (loc) => setLocation(loc);

  const uploadProps = {
    multiple: true,
    fileList,
    onChange: ({ fileList: newList }) => setFileList(newList),
    beforeUpload: (file) => {
      // prevent auto upload; we'll collect files and submit via formData inside saveEmployee thunk (backend expects file buffer)
      return false;
    },
    onRemove: (file) => {
      // allow remove; if it's an existing file (has url) you may want to mark for deletion — add extra logic if backend requires
      setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
    },
  };

  {
    /* ✅ Common helper to normalize Upload event */
  }
  const normFile = (e) => {
    if (Array.isArray(e)) return e;
    return e?.fileList;
  };

  //success check handling helper for quick drawer
  const finalizeQuickFlow = async (employeeId) => {
    if (!employeeId) return;

    // 1️⃣ Close ALL local UI first
    is_login_modal_set(false);
    user_map_modal_set(false);

    // 2️⃣ Fetch employees ONLY once
    const list = await dispatch(fetchEmployees()).unwrap();

    const created = list.find(
      (e) => e.employee_id == employeeId
    );

    if (!created) return;

    // 3️⃣ Notify parent (SearchListAutoComplete)
    onSuccess?.(created);

    // 4️⃣ Close drawer
    onClose?.();
  };



  const handleSave = async () => {
    if (isViewMode) return;

    // 🛑 Block first-time creation if user not mapped/login not enabled
    if (!editing && !isUserMapped && !is_map_login) {
      Swal.fire({
        title: "Action Required",
        text: "To save the employee first time, please enable 'Map User' or 'Enable Login' in the Application Settings for user mapping and login section in this page.",
        icon: "warning",
      });
      return;
    }

    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const values = form.getFieldsValue(true); // get all current form values

      const employee_name = values.full_name;
      let employee_code = values.employee_code;
      const email_address = values.email;
      const joining_date = values.joining_date;
      const department_id = values.department_id;
      const designation_id = values.designation_id;
      const gender = values.gender;

      // ✅ Basic validations
      if (!employee_name?.trim()) {
        Swal.fire({ title: "Employee Name is Required", icon: "warning" });
        return;
      }
      if (!gender) {
        Swal.fire({ title: "Gender is Required", icon: "warning" });
        return;
      }
      if (!email_address?.trim() || !emailRegex.test(email_address)) {
        Swal.fire({ title: "Invalid Email Format", icon: "warning" });
        return;
      }
      if (!joining_date) {
        Swal.fire({ title: "Joining Date is Required", icon: "warning" });
        return;
      }
      if (!department_id) {
        Swal.fire({ title: "Department is Required", icon: "warning" });
        return;
      }
      if (!designation_id) {
        Swal.fire({ title: "Designation is Required", icon: "warning" });
        return;
      }

      // if in employee code is any have missed, regenerate it before saving. 

      if (!employee_code && !editing) {
        try {
          employee_code = await dispatch(fetchEmployeeCode()).unwrap();

          form.setFieldsValue({
            employee_code,
          });
        } catch (err) {
          Swal.fire({
            title: "Unable to generate employee code",
            icon: "error",
          });
          return;
        }
      }

      // ✅ Extract numeric employee_id from code
      const employee_id_num = editing?.employee_id || 0;

      // ================= EXPERIENCE MERGING =================
      const experienceEmployer = experiences.map((e) => e.employer).join(" | ");
      const experienceJobTitle = experiences.map((e) => e.jobTitle).join(" | ");
      const experienceStartDate = experiences
        .map((e) =>
          e.startDate ? dayjs(e.startDate).format("YYYY-MM-DD") : ""
        )
        .join(" | ");
      const experienceEndDate = experiences
        .map((e) => (e.endDate ? dayjs(e.endDate).format("YYYY-MM-DD") : ""))
        .join(" | ");
      const experienceResponsibilities = experiences
        .map((e) => e.responsibilities)
        .join(" | ");
      const experienceAchievements = experiences
        .map((e) => e.achievements)
        .join(" | ");

      // ================= MAIN PAYLOAD =================
      const payload = {
        masterData: {
          action: editing ? "update" : "create",
          employee_code,
          employee_id: employee_id_num,
          company_employee_id: values.company_employee_id,
          employee_name,
          email_address: user_id !== 0 ? selected_user.email : email_address,
          gender_type: values.gender === "female" ? "fmale" : values.gender,
          marital_type: values.marital_status,
          birth_date: values.dob,
          basic_salary: values.basic_salary || 0,
          total_monthly_salary:
            totalMonthlySalary || values.totalMonthlySalary || 0,
          department_id,
          designation_id,
          joining_date: joining_date
            ? dayjs(joining_date).format("YYYY-MM-DD")
            : null,
          experience_employer: experienceEmployer || null,
          experience_job_title: experienceJobTitle || null,
          experience_start_date: experienceStartDate || null,
          experience_end_date: experienceEndDate || null,
          experience_responsibilities: experienceResponsibilities || null,
          experience_achievements: experienceAchievements || null,
          leaves_taken_prev_year: leavesTakenPrevYear || 0,
          leaves_taken_curr_year: leavesTakenCurrYear || 0,
          total_leave_balance: totalLeaveBalance || 0,
          target_amount: values.target_amount || 0,
          commission_per: values.commission_per || 0,
          bank_acc_number: values.bank_acc_number,
          bank_acc_name: values.bank_acc_name,
          bank_name: values.bank_name,
          bank_branch: values.bank_branch,
          iban_no: values.iban_no,
          ifsc_code: values.ifsc_code,
          pan_card: values.pan_card,
          zip_code: values.zip_code,
          reporting_manager: values.reporting_manager || reporting_manager,
          present_address: values.present_address,
          permanent_address: values.permanent_address,
          contact_no: values.phone_no,
          country_code: values.country_code,
          emergency_contact_no: values.emergency_phone,
          emergency_name: values.emergency_name,
          emergency_relation: values.emergency_relation,
          emergency_country_code: values.emergency_country_code,
          emergency_: authInfo?.emergency_,
          work_location: values.work_location || "no",
          employee_status: values.employee_status || "no",
          employment_type: values.employment_type || "no",
          employeeType: values.employee_type || "no",
          job_grade: values.job_grade,
          responsibilities: values.responsibilities,
          performance_reviews: JSON.stringify(performance_reviews),
          goal_objectives: performance_reviews[0]?.goals || "",
          feedback_improvements: performance_reviews[0]?.feedback || "",
          training_development: performance_reviews[0]?.training || "",
          promotion_career: performance_reviews[0]?.promotion || "",
          national_id_no: values.eid_no,
          father_name: values.father_name,
          mother_name: values.mother_name,
          // month: selectedMonth.month,
          // year: selectedYear.year,
          // birth_date: birth_date ? dayjs(birth_date).format("YYYY-MM-DD") : null,
          salary_update_date: salary_update_date ? salary_update_date : null,

          national_id_expiry: values.eid_expiry,
          passport_no: values.passport_no,
          passport_expiry: values.passport_expiry,
          visa_no: values.visa_no,
          visa_expiry: values.visa_expiry,
          visa_issued_different_branch: values.visa_issued_different_branch ? 1 : 0,
          visa_issue_branch_id: values.visa_issued_different_branch
            ? values.visa_issue_branch_id || null
            : null,
          is_different_working_company: values.is_different_working_company ? 1 : 0,
          working_company_name: values.working_company_name || null,
          labour_contract_id: values.labour_contract_id,
          labour_contract_expiry: values.labour_contract_expiry,
          labour_card_id: values.labour_card_id,
          labour_card_expiry: values.labour_card_expiry,
          client_labour_contract_expiry: values.client_labour_contract_expiry,
          insurance_name: values.insurance_name,
          insurance_status: values.insurance_status,
          insurance_expiry: values.insurance_expiry,
          first_reminder: values.first_reminder,
          second_reminder: values.second_reminder,
          user_id,
          linkedin,
          facebook,
          work_projects: workProjects,
          reason_for_leaving,
          exit_interview,
          final_settlement,
          is_contract_completed: isContractCompleted ? 1 : 0,
          contract_completion_date: contractCompletionDate ? contractCompletionDate.format("YYYY-MM-DD") : null,
          hide_salary_details: hideSalaryDetails ? 1 : 0,
          client_name: selectedClient?.name || "",
          client_reporting_manager: clientReportingManager?.id || null,
          use_client_policy,
          company_property_returned: values.company_property_returned,
          employee_max_carry_forward:
            employeeCarryForwardOverride !== null
              ? employeeCarryForwardOverride
              : null,
          use_custom_weekend: useCustomWeekend ? 1 : 0,
          custom_weekend_days: useCustomWeekend && customWeekendDays.length ? customWeekendDays.join(",") : null,


          // ✅ Salary Allowances
          selected_allowances: selectedAllowances || [],
          allowance_values: selectedAllowances?.map((allowance) => {
            const formattedAllowance = allowance?.replace(/\s+/g, "_");
            const allowanceObj = allowanceValues?.find(
              (item) => item.name === formattedAllowance
            );
            return {
              name: formattedAllowance,
              amount: allowanceObj ? allowanceObj.amount : 0,
            };
          }),

          // ✅ Training & Development
          completed_courses: trainingData.completedCourses || "",
          certifications: trainingData.certifications || "",
          upcoming_trainings: trainingData.upcomingTrainings || "",
          skills_development: trainingData.skillsDevelopment || "",
          professional_development: trainingData.professionalDevelopment || "",
          training_mode_online: trainingData.trainingModes?.[0] ? 1 : 0,
          training_mode_in_person: trainingData.trainingModes?.[1] ? 1 : 0,
          training_mode_hybrid: trainingData.trainingModes?.[2] ? 1 : 0,
          future_training_interest: trainingData.futureTrainingInterest ? 1 : 0,

          // ✅ Health & Safety
          medical_records,
          workers_compensation,
          health_assessments,
          health_plans,
          health_assessment_completed:
            health_assessment_completed === "yes" ? 1 : 0,
          first_aid_training: first_aid_training ? 1 : 0,

          // ✅ Add these old UI fields (essential!)
          disciplinary_records: JSON.stringify(disciplinaryRecords),
          disciplinary_actions: disciplinaryRecords[0]?.disciplinaryActions || "",
          legal_documents: disciplinaryRecords[0]?.legalDocuments || "",
          contract_details: disciplinaryRecords[0]?.contractDetails || "",
          action_taken: disciplinaryRecords[0]?.actionTaken || null,
          acknowledged: disciplinaryRecords[0]?.acknowledged || false,
          company_assets: companyAssets || [],
          returned_assets: returnedAssets || {},
          signed_policies: signedPolicies || 0,
          confidentiality_agreements: confidentialityAgreements || 0,
          // user_id: selected_user?.id || 0,
        },
        ...(enable_attendance &&
          selected_day && {
          scheduleAttendanceData: {
            employee_id: employee_id_num,
            client_user_id: clientReportingManager?.id,
            report_day: selected_day,
            ...(use_client_policy && selectedClient
              ? { leave_policy_of_client_id: selectedClient.id }
              : {}),
          },
        }),
      };

      // ================= FORM DATA =================
      const formData = new FormData();
      // formData.append("jsonData", JSON.stringify(payload));
      // Disciplinary document uploads (per record)
      disciplinaryRecords.forEach((record, index) => {
        if (record.documentFile instanceof File) {
          formData.append(`disciplinaryDoc_${index}`, record.documentFile);
        }
      });
      // ✅ File attachments
      const fileFields = {
        employeePhoto: values.employee_photo,
        eidScan: values.eid_scan,
        passportScan: values.passport_scan,
        visaScan: values.visa_scan,
        labourContractIdScan: values.labour_contract_id_scan,
        labourCardScan: values.labour_card_scan,
        labourContractScan: values.labour_contract_scan,
        clientLabourContractScan: values.client_labour_contract_scan,
        insuranceScan: values.insurance_scan,
        labourCancellationCopy: form.getFieldValue("labour_cancellation_copy"),
        visaCancellationCopy: form.getFieldValue("visa_cancellation_copy"),
        experienceLetter: form.getFieldValue("experience_letter"),
        insuranceCancellationCopy: form.getFieldValue("insurance_cancellation_copy"),
        insuranceCancelledRefund: form.getFieldValue("insurance_cancelled_refund"),
        signedFinalSettlementCopy: form.getFieldValue("signed_final_settlement_copy"),

        offerLetterSignedScan: values.offer_letter_signed_scan,
        warningLetter: values.warning_letter,
        memoDocument: values.memo_document,
        otherAgreementsDocument: values.other_agreements_document,
      };

      // ✅ Correctly extract binary file from Ant Design Upload component
      Object.entries(fileFields).forEach(([key, val]) => {
        if (!val) return;
        if (Array.isArray(val)) {
          // If Upload returns an array (fileList)
          const fileObj = val[0]?.originFileObj || val[0];
          if (fileObj instanceof File) formData.append(key, fileObj);
        } else if (val?.originFileObj) {
          // If it's a single upload object
          formData.append(key, val.originFileObj);
        } else if (val instanceof File) {
          // If it's already a File object
          formData.append(key, val);
        }
      });

      // other documents upload
      const otherDocuments = form.getFieldValue("other_documents");
      const existingOtherDocuments = [];
      if (Array.isArray(otherDocuments)) {
        otherDocuments.forEach((file) => {
          // Existing document from DigitalOcean Spaces
          if (file?.url && !file?.originFileObj) {
            existingOtherDocuments.push(file.url);
          }

          // New document selected by user
          const fileObj = file?.originFileObj || file;

          if (fileObj instanceof File) {
            formData.append("otherDocuments", fileObj);
          }
        });
      }
      // send retained existing URLs
      payload.masterData.other_documents =
        existingOtherDocuments.join(",");

      // jsonData must be appended AFTER modifying payload
      formData.append("jsonData", JSON.stringify(payload));


      // ✅ Save via thunk
      const res = await dispatch(saveEmployee(formData)).unwrap();

      if (res?.error) {
        Swal.fire({
          title: res?.message || "Something went wrong.",
          icon: "warning",
        });
      } else {
        Swal.fire({
          title:
            res?.message ||
            (editing ? "Employee updated" : "Employee saved successfully"),
          icon: "success",
        });

        // ✅ refresh notifications (count + list)
        dispatch(loadAllNotifications({}));

        if (onboardingData) {
          const onboardId =
            typeof onboardingData?.id === "object"
              ? onboardingData.id.id
              : onboardingData?.id;
          const convertRes = await dispatch(updateOnboardingStatus(onboardId)).unwrap();
          if (convertRes?.error) {
            message.warning("Employee saved but onboarding not updated");
          }
        }

        dispatch(fetchEmployees());

        if (isQuick) {
          await finalizeQuickFlow(res?.employee_id || res?.data?.employee_id);
          return;
        }

        // full mode fallback
        resetForm();
        onClose?.();
        user_id_set(0);
        user_map_modal_set(false);
        is_map_login_set(false);
        selected_user_set(undefined);
        !isQuick && navigate("/hrpayroll/employees", { replace: true });
        !isQuick && dispatch(fetchEmployeeCode());
      }
    } catch (err) {
      console.error("Save error:", err);
      Swal.fire({ title: "Error saving employee", icon: "error" });
    }
  };

  // Login

  const addLogIn = async () => {
    if (isViewMode) return;

    console.log("authInfo: ", authInfo)

    const selectedBranch = {
      id: authInfo?.userInfo?.branch_id,
      name: authInfo?.userInfo?.branch_name,
    };

    const autoSelectedUserRole = userRoles.find((ur) => ur.label === "employee");

    const selectedUserRole = {
      value: autoSelectedUserRole?.value,
      label: autoSelectedUserRole?.display,
    };

    formSetValues(prev => ({
      ...prev,
      selectedBranch,
      selectedUserRole
    }));


    // ✅ Basic validation
    if (!selectedBranch?.id)
      return message.warning("Please choose a branch");
    if (!selectedUserRole?.value)
      return message.warning("Please choose a user role");

    const email_address = form.getFieldValue("email");
    if (!email_address || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email_address))
      return message.warning("Valid email address required");

    const employee_name = form.getFieldValue("full_name");
    if (!employee_name) return message.warning("Employee name is required");

    const joining_date = form.getFieldValue("joining_date");
    if (!joining_date) return message.warning("Joining Date is required");

    if (!form.getFieldValue("department_id"))
      return message.warning("Department is required");
    if (!form.getFieldValue("designation_id"))
      return message.warning("Designation is required");

    try {
      // 🔹 Extract numeric employee_id
      let employee_code = form.getFieldValue("employee_code");
      const idMatch = employee_code.match(/\d+/);
      const employee_id_num = idMatch
        ? Number(idMatch[0])
        : editing?.employee_id;

      if (!employee_id_num) return message.error("Employee ID missing");

      // 🔹 Experiences (join all with “|”)
      const experienceEmployer = experiences.map((e) => e.employer).join(" | ");
      const experienceJobTitle = experiences.map((e) => e.jobTitle).join(" | ");
      const experienceStartDate = experiences
        .map((e) =>
          e.startDate ? dayjs(e.startDate).format("YYYY-MM-DD") : ""
        )
        .join(" | ");
      const experienceEndDate = experiences
        .map((e) => (e.endDate ? dayjs(e.endDate).format("YYYY-MM-DD") : ""))
        .join(" | ");
      const experienceResponsibilities = experiences
        .map((e) => e.responsibilities)
        .join(" | ");
      const experienceAchievements = experiences
        .map((e) => e.achievements)
        .join(" | ");

      // if employee code is any have missed, regenerate it before saving.
      if (!employee_code && !editing) {
        try {
          employee_code = await dispatch(fetchEmployeeCode()).unwrap();

          form.setFieldsValue({
            employee_code,
          });
        } catch (err) {
          message.error("Unable to generate employee code");
          return;
        }
      }

      // 🔹 Main payload (merged hybrid: Form + State)
      const payload = {
        is_email_sent: true,

        masterData: {
          action: "create",
          employee_code,
          employee_id: employee_id_num,
          company_employee_id: form.getFieldValue("company_employee_id"),
          employee_name,
          email_address,
          gender_type: form.getFieldValue("gender") === "female" ? "fmale" : form.getFieldValue("gender"),
          marital_type: form.getFieldValue("marital_status"),
          basic_salary: form.getFieldValue("basic_salary") || 0,
          total_monthly_salary:
            totalMonthlySalary || form.getFieldValue("totalMonthlySalary") || 0,
          department_id: form.getFieldValue("department_id"),
          designation_id: form.getFieldValue("designation_id"),
          joining_date: joining_date
            ? dayjs(joining_date).format("YYYY-MM-DD")
            : null,
          // birth_date: birth_date ? dayjs(birth_date).format("YYYY-MM-DD") : null,
          birth_date: form.getFieldValue("dob"),

          // ✅ Salary Allowances (stringified for form-data)
          salary_update_date: salary_update_date ? salary_update_date : null,
          selected_allowances:
            selectedAllowances.length > 0
              ? JSON.stringify(selectedAllowances)
              : "[]",
          allowance_values:
            allowanceValues.length > 0
              ? JSON.stringify(
                selectedAllowances.map((allowance) => {
                  const formattedAllowance = allowance?.replace(/\s+/g, "_");
                  const allowanceObj = allowanceValues?.find(
                    (item) => item.name === formattedAllowance
                  );
                  return {
                    name: formattedAllowance,
                    amount: allowanceObj ? allowanceObj.amount : 0,
                  };
                })
              )
              : "{}",

          // ✅ Experience
          experience_employer: experienceEmployer,
          experience_job_title: experienceJobTitle,
          experience_start_date: experienceStartDate,
          experience_end_date: experienceEndDate,
          experience_responsibilities: experienceResponsibilities,
          experience_achievements: experienceAchievements,

          // ✅ Leave and salary
          leaves_taken_prev_year: leavesTakenPrevYear || 0,
          leaves_taken_curr_year: leavesTakenCurrYear || 0,
          total_leave_balance: totalLeaveBalance || 0,
          target_amount: target_amount || 0,
          commission_per: commission_per || 0,

          // ✅ Bank
          bank_acc_number,
          bank_acc_name,
          bank_name,
          bank_branch,
          iban_no,
          ifsc_code,
          pan_card,

          // ✅ Address & contact
          zip_code: form.getFieldValue("zip_code") || "",
          present_address: form.getFieldValue("present_address") || "",
          permanent_address: form.getFieldValue("permanent_address") || "",
          contact_no: form.getFieldValue("phone_no") || "",
          country_code: form.getFieldValue("country_code") || "",
          emergency_contact_no: form.getFieldValue("emergency_phone") || "",
          emergency_name: form.getFieldValue("emergency_name") || "",
          emergency_relation: form.getFieldValue("emergency_relation") || "",
          emergency_country_code:
            form.getFieldValue("emergency_country_code") || "",
          emergency_: authInfo?.emergency_,
          national_id_no: form.getFieldValue("eid_no"),
          father_name: form.getFieldValue("father_name"),
          mother_name: form.getFieldValue("mother_name"),
          // month: selectedMonth.month,
          // year: selectedYear.year,
          national_id_expiry: form.getFieldValue("eid_expiry"),
          passport_no: form.getFieldValue("passport_no"),
          passport_expiry: form.getFieldValue("passport_expiry"),
          visa_no: form.getFieldValue("visa_no"),
          visa_expiry: form.getFieldValue("visa_expiry"),
          visa_issued_different_branch: form.getFieldValue("visa_issued_different_branch") ? 1 : 0,
          visa_issue_branch_id: form.getFieldValue("visa_issued_different_branch")
            ? form.getFieldValue("visa_issue_branch_id") || null
            : null,
          is_different_working_company: form.getFieldValue("is_different_working_company") ? 1 : 0,
          working_company_name: form.getFieldValue("working_company_name") || null,
          labour_contract_id: form.getFieldValue("labour_contract_id"),
          labour_contract_expiry: form.getFieldValue("labour_contract_expiry"),
          labour_card_id: form.getFieldValue("labour_card_id"),
          labour_card_expiry: form.getFieldValue("labour_card_expiry"),
          client_labour_contract_expiry: form.getFieldValue(
            "client_labour_contract_expiry"
          ),
          insurance_name: form.getFieldValue("insurance_name"),
          insurance_status: form.getFieldValue("insurance_status"),
          insurance_expiry: form.getFieldValue("insurance_expiry"),
          first_reminder: form.getFieldValue("first_reminder"),
          second_reminder: form.getFieldValue("second_reminder"),
          reporting_manager,

          // ✅ Job info
          work_location: form.getFieldValue("work_location") || "office",
          employee_status: form.getFieldValue("employee_status") || "active",
          employment_type: form.getFieldValue("employment_type") || "no",
          employeeType: form.getFieldValue("employee_type") || "no",
          job_grade: form.getFieldValue("job_grade") || "",
          responsibilities: form.getFieldValue("responsibilities") || "",
          performance_reviews: JSON.stringify(performance_reviews),
          goal_objectives: performance_reviews[0]?.goals || "",
          feedback_improvements: performance_reviews[0]?.feedback || "",
          training_development: performance_reviews[0]?.training || "",
          promotion_career: performance_reviews[0]?.promotion || "",

          company_property_returned: form.getFieldValue(
            "company_property_returned"
          ),

          // ✅ State fields
          linkedin,
          facebook,
          work_projects:
            form.getFieldValue("work_projects") || workProjects || "",
          reason_for_leaving,
          exit_interview,
          final_settlement,
          is_contract_completed: isContractCompleted ? 1 : 0,
          contract_completion_date: contractCompletionDate ? contractCompletionDate.format("YYYY-MM-DD") : null,
          hide_salary_details: hideSalaryDetails ? 1 : 0,
          client_name: selectedClient?.name || "",
          client_reporting_manager: clientReportingManager?.id || null,
          use_client_policy,
          employee_max_carry_forward:
            employeeCarryForwardOverride !== null
              ? employeeCarryForwardOverride
              : null,
          use_custom_weekend: useCustomWeekend ? 1 : 0,
          custom_weekend_days: useCustomWeekend && customWeekendDays.length ? customWeekendDays.join(",") : null,


          // ✅ Disciplinary, Legal
          disciplinary_records: JSON.stringify(disciplinaryRecords || []),
          disciplinary_actions: disciplinaryRecords[0]?.disciplinaryActions || "",
          legal_documents: disciplinaryRecords[0]?.legalDocuments || "",
          contract_details: disciplinaryRecords[0]?.contractDetails || "",
          action_taken: disciplinaryRecords[0]?.actionTaken || null,
          acknowledged: !!disciplinaryRecords[0]?.acknowledged,

          // ✅ Assets / Policies
          company_assets: companyAssets || [],
          returned_assets: returnedAssets || {},
          signed_policies: signedPolicies || 0,
          confidentiality_agreements: confidentialityAgreements || 0,

          // ✅ Training and Development
          completed_courses: trainingData.completedCourses || "",
          certifications: trainingData.certifications || "",
          upcoming_trainings: trainingData.upcomingTrainings || "",
          skills_development: trainingData.skillsDevelopment || "",
          professional_development: trainingData.professionalDevelopment || "",
          training_mode_online: trainingData.trainingModes?.[0] ? 1 : 0,
          training_mode_in_person: trainingData.trainingModes?.[1] ? 1 : 0,
          training_mode_hybrid: trainingData.trainingModes?.[2] ? 1 : 0,
          future_training_interest: trainingData.futureTrainingInterest ? 1 : 0,

          // ✅ Health & Safety
          medical_records,
          workers_compensation,
          health_assessments,
          health_plans,
          health_assessment_completed:
            health_assessment_completed === "yes" ? 1 : 0,
          first_aid_training: first_aid_training ? 1 : 0,
        },

        // ✅ Login creation info
        signUpData: {
          ...formValues,
          selectedBranch,
          selectedUserRole,
          email: email_address,
          full_name: employee_name,
          acc_type: "employee",
          name: employee_name, // fallback username
          password: formValues.password || "", //old flow. now not using
          user_confirm_password: formValues.user_confirm_password || "", //old flow. now not using
        },

        // ✅ Optional Attendance Mapping
        ...(enable_attendance &&
          selected_day && {
          scheduleAttendanceData: {
            employee_id: employee_id_num,
            client_user_id: clientReportingManager?.id,
            report_day: selected_day,
            ...(use_client_policy && selectedClient
              ? { leave_policy_of_client_id: selectedClient.id }
              : {}),
          },
        }),
      };

      // 🔹 Prepare FormData for uploads
      const formData = new FormData();
      formData.append("jsonData", JSON.stringify(payload));
      // Disciplinary document uploads (per record)
      disciplinaryRecords.forEach((record, index) => {
        if (record.documentFile instanceof File) {
          formData.append(`disciplinaryDoc_${index}`, record.documentFile);
        }
      });
      const uploadFields = {
        employeePhoto: form.getFieldValue("employee_photo"),
        eidScan: form.getFieldValue("eid_scan"),
        passportScan: form.getFieldValue("passport_scan"),
        visaScan: form.getFieldValue("visa_scan"),
        labourContractScan: form.getFieldValue("labour_contract_scan"),
        labourCardScan: form.getFieldValue("labour_card_scan"),
        clientLabourContractScan: form.getFieldValue(
          "client_labour_contract_scan"
        ),
        labourCancellationCopy: form.getFieldValue("labour_cancellation_copy"),
        visaCancellationCopy: form.getFieldValue("visa_cancellation_copy"),
        experienceLetter: form.getFieldValue("experience_letter"),
        insuranceScan: form.getFieldValue("insurance_scan"),
        insuranceCancellationCopy: form.getFieldValue("insurance_cancellation_copy"),
        insuranceCancelledRefund: form.getFieldValue("insurance_cancelled_refund"),
        signedFinalSettlementCopy: form.getFieldValue("signed_final_settlement_copy "),
        offerLetterSignedScan: form.getFieldValue("offer_letter_signed_scan"),
        warningLetter: form.getFieldValue("warning_letter"),         // ✅ add
        memoDocument: form.getFieldValue("memo_document"),           // ✅ add
        otherAgreementsDocument: form.getFieldValue("other_agreements_document"),
      };
      // ✅ Extract the binary correctly
      Object.entries(uploadFields).forEach(([key, val]) => {
        if (!val) return;
        if (Array.isArray(val)) {
          const fileObj = val[0]?.originFileObj || val[0];
          if (fileObj instanceof File) formData.append(key, fileObj);
        } else if (val?.originFileObj) {
          formData.append(key, val.originFileObj);
        } else if (val instanceof File) {
          formData.append(key, val);
        }
      });

      // other documents upload
      const otherDocuments = form.getFieldValue("other_documents");

      if (Array.isArray(otherDocuments)) {
        const existingOtherDocumentUrls = [];

        otherDocuments.forEach((file) => {
          if (file?.url && !file?.originFileObj) {
            existingOtherDocumentUrls.push(file.url);
          }

          const fileObj = file?.originFileObj || file;

          if (fileObj instanceof File) {
            formData.append("otherDocuments", fileObj);
          }
        });

        formData.append(
          "existingOtherDocuments",
          JSON.stringify(existingOtherDocumentUrls)
        );
      }

      // 🔹 Save to backend
      const res = await dispatch(saveEmployeeUser(formData)).unwrap();

      if (res?.error) message.warning(res?.message || "Something went wrong.");
      else {
        message.success(res?.message || "Login access created successfully.");
        is_map_login_set(true);
        is_login_modal_set(false);
        formSetValues({ selectedBranch: null, selectedUserRole: null });
        is_email_sent_set(false);
        if (onboardingData) {
          const onboardId =
            typeof onboardingData?.id === "object"
              ? onboardingData.id.id
              : onboardingData?.id;
          const convertRes = await dispatch(updateOnboardingStatus(onboardId)).unwrap();
          if (convertRes?.error) {
            message.warning("Employee saved but onboarding not updated");
          }
        }
        dispatch(fetchEmployees());
        if (isQuick) {
          await finalizeQuickFlow(employee_id_num);
          return;
        }
        //FULL MODE
        resetForm();
      }

      // 🔹 Cleanup
      formSetValues({ selectedBranch: null, selectedUserRole: null });
      is_email_sent_set(false);
      !isQuick && navigate("/hrpayroll/employees", { replace: true });
      !isQuick && dispatch(fetchEmployeeCode());
    } catch (err) {
      console.error("Error in addLogIn:", err);
      message.error("Failed to enable login access.");
    }
  };

  const resetForm = () => {
    form.resetFields();
    setFileList([]);
    setSelectedClient(null);
    setClientReportingManager(null);
    setCompanyAssets([]);
    setReturnedAssets({});
    setSelectedAllowances([]);
    setAllowanceValues([]);
    setTotalMonthlySalary(0);
    setTotalAnnualSalary(0);
    setLeavesTakenPrevYear(0);
    setLeavesTakenCurrYear(0);
    target_amount_set(0);
    commission_per_set(0);
    enable_attendance_set(false);
    selected_day_set(null);
    use_client_policy_set(false);
    setLinkedin("");
    setFacebook("");
    setWorkProjects("");
    reason_for_leaving_set("");
    exit_interview_set("");
    final_settlement_set("");
    setContractCompletionDate("");
    setEmployee_id(0);
    setSelectedAllowances([]);
    setAllowanceValues([]);
    setExperiences([
      {
        employer: "",
        jobTitle: "",
        startDate: "",
        endDate: "",
        responsibilities: "",
        achievements: "",
      },
    ]);
    setTrainingData({
      completedCourses: "",
      certifications: "",
      upcomingTrainings: "",
      skillsDevelopment: "",
      professionalDevelopment: "",
      trainingModes: [false, false, false],
      futureTrainingInterest: false,
    });
    first_aid_training_set(false);
    setLeavesTakenPrevYear(0);
    setLeavesTakenCurrYear(0);
    // performance_reviews_set("");
    // goal_objectives_set("");
    // feedback_improvements_set("");
    // training_development_set("");
    // promotion_career_set("");
    performance_reviews_set([
      { date: null, review: "", goals: "", feedback: "", training: "", promotion: "" }
    ]);
    salary_update_date_set("");
    user_id_set(0);
    setLinkedin("");
    setFacebook("");
    setSelectedClient("");
    setClientReportingManager(null);
    use_client_policy_set(false);
    medical_records_set("");
    workers_compensation_set("");
    health_assessments_set("");
    health_plans_set("");
    set_health_assessment_completed("");
    setDisciplinaryRecords([{
      date: null,
      disciplinaryActions: "",
      legalDocuments: "",
      contractDetails: "",
      actionTaken: "",
      acknowledged: false,
      documentFile: null,
    }]);
    setCompanyAssets([]);
    setReturnedAssets({});
    setSignedPolicies(false);
    setConfidentialityAgreements(false);
    selected_user_set(null);
  };

  // drafting for Add New Buttons in drop down
  const handleSaveDraftAndNavigate = (path) => {
    if (!form) return;

    const formValues = form.getFieldsValue(true);

    if (!formValues.employee_code && code) {
      formValues.employee_code = code;
    }

    // 🔹 Format Experiences safely and consistently
    const experienceEmployer = (experiences || [])
      .map((e) => e?.employer || "")
      .join(" | ");
    const experienceJobTitle = (experiences || [])
      .map((e) => e?.jobTitle || "")
      .join(" | ");
    const experienceStartDate = (experiences || [])
      .map((e) => (e?.startDate ? dayjs(e.startDate).format("YYYY-MM-DD") : ""))
      .join(" | ");
    const experienceEndDate = (experiences || [])
      .map((e) => (e?.endDate ? dayjs(e.endDate).format("YYYY-MM-DD") : ""))
      .join(" | ");
    const experienceResponsibilities = (experiences || [])
      .map((e) => e?.responsibilities || "")
      .join(" | ");
    const experienceAchievements = (experiences || [])
      .map((e) => e?.achievements || "")
      .join(" | ");

    // 🔹 Salary Allowances
    const formattedAllowances = (selectedAllowances || []).map((allowance) => {
      const formattedName = allowance?.replace(/\s+/g, "_");
      const allowanceObj = (allowanceValues || []).find(
        (item) => item.name === formattedName
      );
      return {
        name: formattedName,
        amount: allowanceObj ? allowanceObj.amount : 0,
      };
    });

    // 🔹 Gather all non-form controlled states
    const extraStates = {
      // ✅ Salary / Allowances
      salary_update_date: salary_update_date || null,
      selected_allowances: JSON.stringify(selectedAllowances || []),
      allowance_values: JSON.stringify(formattedAllowances || []),

      // ✅ Experience
      experience_employer: experienceEmployer,
      experience_job_title: experienceJobTitle,
      experience_start_date: experienceStartDate,
      experience_end_date: experienceEndDate,
      experience_responsibilities: experienceResponsibilities,
      experience_achievements: experienceAchievements,

      // ✅ Leave and Salary
      leaves_taken_prev_year: leavesTakenPrevYear || 0,
      leaves_taken_curr_year: leavesTakenCurrYear || 0,
      total_leave_balance: totalLeaveBalance || 0,
      target_amount: target_amount || 0,
      commission_per: commission_per || 0,

      // ✅ Bank
      bank_acc_number: bank_acc_number || "",
      bank_acc_name: bank_acc_name || "",
      bank_name: bank_name || "",
      bank_branch: bank_branch || "",
      iban_no: iban_no || "",
      ifsc_code: ifsc_code || "",
      pan_card: pan_card || "",
      reporting_manager: reporting_manager || "",

      // ✅ Performance
      performance_reviews: JSON.stringify(performance_reviews || []),
      goal_objectives: performance_reviews[0]?.goals || "",
      feedback_improvements: performance_reviews[0]?.feedback || "",
      training_development: performance_reviews[0]?.training || "",
      promotion_career: performance_reviews[0]?.promotion || "",
      // ✅ Client Details
      linkedin: linkedin || "",
      facebook: facebook || "",
      work_projects: workProjects || "",
      reason_for_leaving: reason_for_leaving || "",
      exit_interview: exit_interview || "",
      final_settlement: final_settlement || "",
      contract_completion_date: contractCompletionDate.format("YYYY-MM-DD"),
      hide_salary_details: !!hideSalaryDetails,
      client_name: selectedClient?.name || "",
      client_reporting_manager: clientReportingManager?.id || null,
      use_client_policy: !!use_client_policy,

      // ✅ Disciplinary / Legal
      disciplinary_records: JSON.stringify(disciplinaryRecords || []),
      disciplinary_actions: disciplinaryRecords[0]?.disciplinaryActions || "",
      legal_documents: disciplinaryRecords[0]?.legalDocuments || "",
      contract_details: disciplinaryRecords[0]?.contractDetails || "",
      action_taken: disciplinaryRecords[0]?.actionTaken || null,
      acknowledged: !!disciplinaryRecords[0]?.acknowledged,
      // ✅ Assets / Policies
      company_assets: companyAssets || [],
      returned_assets: returnedAssets || {},
      signed_policies: signedPolicies || 0,
      confidentiality_agreements: confidentialityAgreements || 0,

      // ✅ Training & Development
      completed_courses: trainingData?.completedCourses || "",
      certifications: trainingData?.certifications || "",
      upcoming_trainings: trainingData?.upcomingTrainings || "",
      skills_development: trainingData?.skillsDevelopment || "",
      professional_development: trainingData?.professionalDevelopment || "",
      training_mode_online: trainingData?.trainingModes?.[0] ? 1 : 0,
      training_mode_in_person: trainingData?.trainingModes?.[1] ? 1 : 0,
      training_mode_hybrid: trainingData?.trainingModes?.[2] ? 1 : 0,
      future_training_interest: trainingData?.futureTrainingInterest ? 1 : 0,

      // ✅ Health & Safety
      medical_records: medical_records || "",
      workers_compensation: workers_compensation || "",
      health_assessments: health_assessments || "",
      health_plans: health_plans || "",
      health_assessment_completed:
        health_assessment_completed === "yes" ? 1 : 0,
      first_aid_training: !!first_aid_training,
    };

    // Add return URL as query param so we know where to go back
    //  const returnUrl = encodeURIComponent(urlLocation?.pathname);

    // Utility: remove non-serializable data like File, Blob, dayjs, etc.
    const deepClone = (obj) => {
      return JSON.parse(
        JSON.stringify(obj, (key, value) => {
          // Convert problematic types before stringify
          if (dayjs.isDayjs(value)) {
            return value.toISOString();
          }
          if (!dayjs.isDayjs(value)) {
            // If 'value' is not a Day.js object, try parsing it
            const dayjsValue = dayjs(value);
            if (dayjsValue.isValid()) {
              // Check if the parsed value is valid
              return dayjsValue.toISOString();
            } else {
              // Handle invalid date case, e.g., return null or throw an error
              console.error("Invalid date value:", value);
              return null;
            }
          }
          if (value instanceof Date) return value.toISOString();
          if (value instanceof File || value instanceof Blob) return null;
          return value;
        })
      );
    };

    const sanitizeForRedux = (obj) => {
      if (obj == null) return obj;
      if (Array.isArray(obj)) return obj.map(sanitizeForRedux);
      if (typeof obj === "object") {
        return Object.fromEntries(
          Object.entries(obj).map(([k, v]) => [k, sanitizeForRedux(v)])
        );
      }
      return obj;
    };

    // 🔹 In your save handler
    const rawValues = form.getFieldsValue(true);
    const clonedValues = deepClone(rawValues);
    const sanitizedData = sanitizeForRedux({ ...clonedValues, ...extraStates });
    dispatch(saveDraft({ key: DRAFT_KEY, data: sanitizedData }));

    // Open add-new page in new tab, with returnUrl
    // window.open(`${path}?returnUrl=${returnUrl}`, "_blank");
    // window.open(`${path}?returnUrl=${returnUrl}`, "_self");

    navigate(`${path}?returnUrl=${urlLocation?.pathname}`);
  };

  const setExtraStates = (restored) => {
    // ADD inside setExtraStates:
    if (restored.disciplinary_records) {
      try {
        const parsed = typeof restored.disciplinary_records === "string"
          ? JSON.parse(restored.disciplinary_records)
          : restored.disciplinary_records;
        if (Array.isArray(parsed)) setDisciplinaryRecords(parsed);
      } catch {
        // ignore
      }
    }
    if (restored.performance_reviews) {
      try {
        const parsed = typeof restored.performance_reviews === "string"
          ? JSON.parse(restored.performance_reviews)
          : restored.performance_reviews;
        if (Array.isArray(parsed)) performance_reviews_set(parsed);
      } catch {
        // ignore
      }
    }
    if (restored.experiences) setExperiences(restored.experiences);
    if (restored.selectedAllowances)
      setSelectedAllowances(restored.selectedAllowances);
    if (restored.allowanceValues) setAllowanceValues(restored.allowanceValues);
    if (restored.selectedClient) setSelectedClient(restored.selectedClient);
    if (restored.clientReportingManager)
      setClientReportingManager(restored.clientReportingManager);
    if (restored.trainingData) setTrainingData(restored.trainingData);
    if (restored.companyAssets) setCompanyAssets(restored.companyAssets);
    if (restored.returnedAssets) setReturnedAssets(restored.returnedAssets);
    if (restored.leavesTakenPrevYear !== undefined)
      setLeavesTakenPrevYear(restored.leavesTakenPrevYear);
    if (restored.leavesTakenCurrYear !== undefined)
      setLeavesTakenCurrYear(restored.leavesTakenCurrYear);
    // if (restored.totalLeaveBalance !== undefined) setTotalLeaveBalance(restored.totalLeaveBalance);
    if (restored.hideSalaryDetails !== undefined)
      setHideSalaryDetails(restored.hideSalaryDetails);
    if (restored.use_client_policy !== undefined)
      use_client_policy_set(restored.use_client_policy);
    if (restored.health_assessment_completed)
      set_health_assessment_completed(restored.health_assessment_completed);
    if (restored.first_aid_training !== undefined)
      first_aid_training_set(restored.first_aid_training);
    dispatch(clearDraft(DRAFT_KEY)); // manually clear after restore
  };

  useRestoreDraft(form, DRAFT_KEY, {
    autoClear: true,
    setExtraStates,
    shouldRestore,
  });

  const ALLOWANCES = [
    "Basic Salary",
    "Housing Allowance",
    "Transportation Allowance",
    "Education Allowance",
    "Monthly Bonus",
    "Yearly Bonus",
    "Phone Allowance",
    "House Rent Allowance",
  ];

  const handleAllowanceSelect = (value) => {
    if (!selectedAllowances.includes(value)) {
      setSelectedAllowances([...selectedAllowances, value]);
      setAllowanceValues([
        ...allowanceValues,
        { name: value.replace(/\s+/g, "_"), amount: 0 },
      ]);
    }
  };

  const handleAllowanceValueChange = (allowance, amount) => {
    const key = allowance.replace(/\s+/g, "_");
    const updated = allowanceValues.map((item) =>
      item.name === key ? { ...item, amount: parseFloat(amount) || 0 } : item
    );
    setAllowanceValues(updated);
    recalculateTotals(updated);
  };

  const handleAllowanceRemove = (allowance) => {
    const key = allowance.replace(/\s+/g, "_");
    const newSelected = selectedAllowances.filter((a) => a !== allowance);
    const newValues = allowanceValues.filter((item) => item.name !== key);
    setSelectedAllowances(newSelected);
    setAllowanceValues(newValues);
    recalculateTotals(newValues);
  };

  const handleKeyDownOnAllowance = (e) => {
    if (e.key === "Enter" || e.key === "Tab") setEditingAllowance(null);
  };

  const recalculateTotals = (values) => {
    let monthlyTotal = 0;
    let annualTotal = 0;

    values.forEach((v) => {
      const amount = Number(v.amount || 0);
      const name = v.name?.toLowerCase?.() || "";

      // 👇 treat bonuses and yearly items as annual-only
      if (name.includes("yearly") || name.includes("annual")) {
        annualTotal += amount; // add only to annual
      } else {
        monthlyTotal += amount; // add to monthly
        annualTotal += amount * 12; // also contributes to annual via monthly x12
      }
    });

    setTotalMonthlySalary(monthlyTotal);
    setTotalAnnualSalary(annualTotal);
  };

  // When checkbox is clicked, show schedule day modal
  const handleAttendanceCheckbox = (e) => {
    if (selectedClient == null) {
      Swal.fire({
        title: "Select Client",
        icon: "warning",
      });
      return false;
    } else if (clientReportingManager == null) {
      Swal.fire({
        title: `Select Reporting Manager`,
        icon: "warning",
      });
      return false;
    }
    enable_attendance_set(e.target.checked);
    if (e.target.checked) {
      setIsAttendanceModalOpen(true);
    } else {
      setIsAttendanceModalOpen(false);
    }
  };

  // Save the selected day
  const handleDaySelection = (day) => {
    selected_day_set(day);
  };

  // client attendance schedule modal features ends

  // const handleUserMapping = () => {
  //   mapped_user_set(true);
  //   saveFormAction(true);
  // };

  const handleExperienceChange = (index, field, value) => {
    const updated = [...experiences];
    updated[index][field] = value;
    setExperiences(updated);
  };

  const addExperience = () => {
    setExperiences([
      ...experiences,
      {
        employer: "",
        jobTitle: "",
        startDate: null,
        endDate: null,
        responsibilities: "",
        achievements: "",
      },
    ]);
  };
  const addPerformanceReview = () => {
    performance_reviews_set([
      ...performance_reviews,
      { date: null, review: "", goals: "", feedback: "", training: "", promotion: "" }
    ]);
  };
  const addDisciplinaryRecord = () => {
    setDisciplinaryRecords([
      ...disciplinaryRecords,
      {
        date: null,
        disciplinaryActions: "",
        legalDocuments: "",
        contractDetails: "",
        actionTaken: "",
        acknowledged: false,
        documentFile: null,
      }
    ]);
  };

  const removeDisciplinaryRecord = (index) => {
    if (disciplinaryRecords.length === 1) return;
    setDisciplinaryRecords(disciplinaryRecords.filter((_, i) => i !== index));
  };

  const handleDisciplinaryChange = (index, field, value) => {
    const updated = [...disciplinaryRecords];
    updated[index] = { ...updated[index], [field]: value };
    setDisciplinaryRecords(updated);
  };
  const removePerformanceReview = (index) => {
    if (performance_reviews.length === 1) return; // keep at least one
    performance_reviews_set(performance_reviews.filter((_, i) => i !== index));
  };

  const handlePerformanceReviewChange = (index, field, value) => {
    const updated = [...performance_reviews];
    updated[index] = { ...updated[index], [field]: value };
    performance_reviews_set(updated);
  };
  const removeExperience = (index) => {
    const updated = experiences.filter((_, i) => i !== index);
    setExperiences(updated);
  };

  const handleHealthAssessmentRadio = (e) => {
    set_health_assessment_completed(e.target.value);
  };

  const addAsset = () => {
    if (newAsset && !companyAssets.includes(newAsset)) {
      setCompanyAssets([...companyAssets, newAsset]);
      setNewAsset("");
    }
  };

  const toggleReturnStatus = (asset) => {
    setReturnedAssets((prev) => ({
      ...prev,
      [asset]: !prev[asset],
    }));
  };

  const handleUserMapping = () => {
    mapped_user_set(true);
    is_map_login_set(true);
    handleSave(true);
  };

  const onCancel = () => {
    dispatch(fetchEmployees());
    onClose?.();
    resetForm();
    user_id_set(0);
    user_map_modal_set(false);
    is_map_login_set(false);
    selected_user_set(undefined);
    setEditingRecord(null)
    // isEditSet(false);
  };

  return (
    <div className="employee-form" style={{ position: "relative" }}>
      {!isQuick && <Title level={4}>{editing ? "Edit Employee" : "New Employee"}</Title>}
      <Form
        form={form}
        layout="vertical"
        requiredMark={(label, { required }) =>
          required ? (
            <>
              {label} <span style={{ color: "#ff4d4f" }}>*</span>
            </>
          ) : (
            label
          )
        }
        onFinish={handleSave}
        initialValues={{
          phone: { country_code: "971", contact_no: "" },
          employee_status: "active",
          employee_type: "permanent",
        }}
        style={{
          "--form-item-margin-bottom": "8px",
        }}
      >
        <EmployeeFormQuickNav onNavigate={openPanel} />
        <Collapse activeKey={activeCollapseKey} onChange={(key) => setActiveCollapseKey(Array.isArray(key) ? key : [key])} accordion>
          {/* ================= PERSONAL INFORMATION ================= */}
          <Panel
            className="emp-panel-anchor-personal"
            header={<PanelHeader panelKey="personal" title="Personal Information" required categoryKey="personal_contact" />}
            key="personal"
          >
            <div
              style={{
                border: "1px solid #f0f0f0",
                padding: 16,
                borderRadius: 8,
                marginBottom: 16,
                background: "#fafafa",
              }}
            >
              {hasEmployeeHistory && (
                <Row justify="end" style={{ marginBottom: 16 }}>
                  <Tooltip title="Download Entire Employee Documents and Designation History Sheet in Excel">
                    <Button
                      type="primary"
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={() =>
                        downloadExcel("employee", editing?.employee_id)
                      }
                    >
                      <HistoryOutlined />
                    </Button>
                  </Tooltip>
                </Row>
              )}

              <Row gutter={12}>
                {/* Top row */}
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Employee Code"
                    name="employee_code"
                    rules={[
                      { required: true, message: "Employee code required" },
                    ]}
                  >
                    <Input disabled placeholder="Employee Code" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Company Employee ID"
                    name="company_employee_id"
                  >
                    <Input
                      placeholder="Company Employee ID"
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Employee Name"
                    name="full_name"
                    rules={[
                      { required: true, message: "Employee name required" },
                    ]}
                  >
                    <Input placeholder="Employee Name" disabled={viewMode || isViewMode} />
                  </Form.Item>
                </Col>
                {!isFieldHidden("Employee Photo") &&
                  <>
                    {!isQuick && (<Col xs={24} sm={8}>
                      <Form.Item
                        label="Employee Photo"
                        name="employee_photo"
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                      >
                        <Upload
                          listType="picture"
                          maxCount={1}
                          beforeUpload={() => false}
                          accept=".jpg,.jpeg,.png,.pdf"
                          disabled={viewMode || isViewMode}
                        >
                          <Button icon={<UploadOutlined />}>Upload</Button>
                        </Upload>
                      </Form.Item>
                    </Col>)}
                  </>
                }
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Gender Type"
                    name="gender"
                    rules={[{ required: true, message: "Gender required" }]}
                  >
                    <Radio.Group disabled={viewMode || isViewMode}>
                      <Radio value="male">Male</Radio>
                      <Radio value="female">Female</Radio>
                      <Radio value="others">Others</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Marital Status" name="marital_status">
                    <Radio.Group disabled={viewMode || isViewMode}>
                      <Radio value="single">Single</Radio>
                      <Radio value="married">Married</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>)}

                <Col xs={24} sm={8}>
                  <Form.Item label="Date of Birth" name="dob">
                    <DatePicker
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Email Address"
                    name="email"
                    rules={[
                      {
                        required: true,
                        type: "email",
                        message: "Invalid email",
                      },
                    ]}
                  >
                    <Input placeholder="Email Address" disabled={viewMode || isViewMode} />
                  </Form.Item>
                </Col>

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Zip Code" name="zip_code">
                    <Input placeholder="Zip Code" disabled={viewMode || isViewMode} />
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Present Address" name="present_address">
                    <Input placeholder="Present Address" disabled={viewMode || isViewMode} />
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Permanent Address" name="permanent_address">
                    <Input
                      placeholder="Permanent Address"
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>)}

                {/* Contact Number */}
                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Contact No" >
                    <Form.Item name="country_code" noStyle>
                      <Input type="hidden" disabled={viewMode || isViewMode} />
                    </Form.Item>
                    <Form.Item name="phone_no" noStyle>
                      <Input type="hidden" disabled={viewMode || isViewMode} />
                    </Form.Item>
                    <ReactPhoneInput
                      country={defaultCountry}
                      value={
                        form.getFieldValue("country_code")
                          ? `+${form.getFieldValue("country_code")} ${form.getFieldValue("phone_no") || ""
                          }`
                          : ""
                      }
                      inputStyle={{ width: "100%" }}
                      onChange={(value, countryData) => {
                        const code = countryData.dialCode;
                        const phone = value.replace(`+${code}`, "").trim();
                        const clean = phone.startsWith(code)
                          ? phone.slice(code.length)
                          : phone;
                        form.setFieldsValue({
                          country_code: code,
                          phone_no: clean,
                        });
                      }}
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Father Name" name="father_name">
                    <Input placeholder="Father Name" disabled={viewMode || isViewMode} />
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Mother Name" name="mother_name">
                    <Input placeholder="Mother Name" disabled={viewMode || isViewMode} />
                  </Form.Item>
                </Col>)}

                {/* Emergency Contact */}
                {!isQuick && (<Col xs={24}>
                  <Form.Item label="Emergency Contact">
                    <Row gutter={8}>
                      {!isQuick && (<Col xs={24} sm={8}>
                        <Form.Item name="emergency_name" noStyle>
                          <Input
                            placeholder="Emergency Name"
                            disabled={viewMode || isViewMode}
                          />
                        </Form.Item>
                      </Col>)}
                      {!isQuick && (<Col xs={24} sm={8} className="emergencyRelationshipField">
                        <Form.Item name="emergency_relation" noStyle>
                          <Input
                            placeholder="Relationship"
                            disabled={viewMode || isViewMode}
                          />
                        </Form.Item>
                      </Col>)}
                      {!isQuick && (<Col xs={24} sm={8}>
                        <Form.Item name="emergency_country" noStyle>
                          <Input type="hidden" />
                        </Form.Item>

                        <Form.Item name="emergency_phone" noStyle>
                          <Input type="hidden" />
                        </Form.Item>
                        <ReactPhoneInput
                          country={defaultCountry}
                          value={
                            form.getFieldValue("emergency_country")
                              ? `+${form.getFieldValue("emergency_country")} ${form.getFieldValue("emergency_phone") || ""}`
                              : ""
                          }
                          inputStyle={{ width: "100%" }}
                          onChange={(value, countryData) => {
                            const code = countryData.dialCode;
                            const phone = value.replace(`+${code}`, "").trim();

                            const clean = phone.startsWith(code)
                              ? phone.slice(code.length)
                              : phone;

                            form.setFieldsValue({
                              emergency_country: code,
                              emergency_phone: clean,
                            });
                          }}
                          disabled={viewMode || isViewMode}
                        />
                      </Col>)}
                    </Row>
                  </Form.Item>
                </Col>)}

                {/* ID, Passport, Visa, Labour, Insurance fields */}
                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="EID / National ID No" name="eid_no">
                    <Input
                      placeholder="EID / National ID No"
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="EID Expiry Date" name="eid_expiry">
                    <DatePicker
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                      id="eid_expiry"
                      placeholder="EID Expiry Date"
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  {!isFieldHidden("EID / National ID Scan Copy") && (
                    <Form.Item
                      label="EID / National ID Scan Copy"
                      name="eid_scan"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                    >
                      <Upload
                        beforeUpload={() => false}
                        maxCount={1}
                        accept=".jpg,.jpeg,.png,.pdf"
                        disabled={viewMode || isViewMode}
                      >
                        <Button icon={<UploadOutlined />}>Upload</Button>
                      </Upload>
                    </Form.Item>
                  )}
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Passport No" name="passport_no">
                    <Input placeholder="Passport No" disabled={viewMode || isViewMode} />
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item
                    label="Passport Expiry Date"
                    name="passport_expiry"
                  >
                    <DatePicker
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                      placeholder="Passport Expiry Date"
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  {!isFieldHidden("Passport Scan Copy") && (
                    <Form.Item
                      label="Passport Scan Copy"
                      name="passport_scan"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                    >
                      <Upload
                        beforeUpload={() => false}
                        maxCount={1}
                        accept=".jpg,.jpeg,.png,.pdf"
                        disabled={viewMode || isViewMode}
                      >
                        <Button icon={<UploadOutlined />}>Upload</Button>
                      </Upload>
                    </Form.Item>
                  )}
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Visa No" name="visa_no">
                    <Input placeholder="Visa No" disabled={viewMode || isViewMode} />
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Visa Expiry Date" name="visa_expiry">
                    <DatePicker
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                      placeholder="Visa Expiry Date"
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  {!isFieldHidden("Visa Scan Copy") && (
                    <Form.Item
                      label="Visa Scan Copy"
                      name="visa_scan"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                    >
                      <Upload
                        beforeUpload={() => false}
                        maxCount={1}
                        accept=".jpg,.jpeg,.png,.pdf"
                        disabled={viewMode || isViewMode}
                      >
                        <Button icon={<UploadOutlined />}>Upload</Button>
                      </Upload>
                    </Form.Item>
                  )}
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item
                    name="visa_issued_different_branch"
                    valuePropName="checked"
                  >
                    <Checkbox
                      disabled={viewMode || isViewMode}
                      onChange={(e) => {
                        if (!e.target.checked) {
                          form.setFieldValue("visa_issue_branch_id", null);
                        }
                      }}
                    >
                      Visa issued in different branch
                    </Checkbox>
                  </Form.Item>

                  <Form.Item
                    shouldUpdate={(prev, curr) =>
                      prev.visa_issued_different_branch !==
                      curr.visa_issued_different_branch
                    }
                  >
                    {({ getFieldValue }) =>
                      getFieldValue("visa_issued_different_branch") ? (
                        <Form.Item
                          name="visa_issue_branch_id"
                          label="Visa Issue Branch"
                          rules={[
                            {
                              required: true,
                              message: "Please select the branch where the visa was issued",
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            placeholder="Select Branch"
                            optionFilterProp="label"
                            options={branches.map((b) => ({
                              label: b.name,
                              value: b.id,
                            }))}
                            disabled={viewMode || isViewMode}
                          />
                        </Form.Item>
                      ) : null
                    }
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={16}>
                  <Form.Item
                    name="is_different_working_company"
                    valuePropName="checked"

                  >
                    <Checkbox
                      disabled={viewMode || isViewMode}
                      onChange={(e) => {
                        if (!e.target.checked) {
                          form.setFieldValue("working_company_name", null);
                        }
                      }}>
                      Employee working under different company
                    </Checkbox>
                  </Form.Item>

                  <Form.Item
                    shouldUpdate={(prev, curr) =>
                      prev.is_different_working_company !==
                      curr.is_different_working_company
                    }
                  >
                    {({ getFieldValue }) =>
                      getFieldValue("is_different_working_company") ? (
                        <Form.Item
                          name="working_company_name"
                          label="Working Company Name"
                          rules={[
                            {
                              required: true,
                              message: "Please enter working company name",
                            },
                          ]}
                        >
                          <Input placeholder="Enter working company name" disabled={viewMode || isViewMode} />
                        </Form.Item>
                      ) : null
                    }
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  {!isFieldHidden("Visa Scan Copy") && (
                    <Form.Item
                      label="Offer Letter Signed Copy"
                      name="offer_letter_signed_scan"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                    >
                      <Upload
                        beforeUpload={() => false}
                        maxCount={1}
                        accept=".jpg,.jpeg,.png,.pdf"
                        disabled={viewMode || isViewMode}
                      >
                        <Button icon={<UploadOutlined />}>Upload</Button>
                      </Upload>
                    </Form.Item>
                  )}
                </Col>)}

                {!isQuick && isAdminAndHrUser && (<Col xs={24} sm={8}>
                  <Form.Item
                    label="Labour Contract ID"
                    name="labour_contract_id"
                  >
                    <Input disabled={viewMode || isViewMode} />
                  </Form.Item>
                </Col>)}
                {!isQuick && isAdminAndHrUser && (<Col xs={24} sm={8}>
                  <Form.Item
                    label="Labour Contract Expiry Date"
                    name="labour_contract_expiry"
                  >
                    <DatePicker
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>)}

                {!isQuick && isAdminAndHrUser && (<Col xs={24} sm={8}>
                  {!isFieldHidden("Labour Contract Scan Copy") && (
                    <Form.Item
                      label="Labour Contract Scan Copy"
                      name="labour_contract_scan"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                    >
                      <Upload
                        beforeUpload={() => false}
                        maxCount={1}
                        accept=".jpg,.jpeg,.png,.pdf"
                        disabled={viewMode || isViewMode}
                      >
                        <Button icon={<UploadOutlined />}>Upload</Button>
                      </Upload>
                    </Form.Item>
                  )}
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item
                    label="Labour Card ID"
                    name="labour_card_id"
                  >
                    <Input disabled={viewMode || isViewMode} />
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item
                    label="Labour Card Expiry Date"
                    name="labour_card_expiry"
                  >
                    <DatePicker
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  {!isFieldHidden("Labour Card Scan Copy") && (
                    <Form.Item
                      label="Labour Card Scan Copy"
                      name="labour_card_scan"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                    >
                      <Upload
                        beforeUpload={() => false}
                        maxCount={1}
                        accept=".jpg,.jpeg,.png,.pdf"
                        disabled={viewMode || isViewMode}
                      >
                        <Button icon={<UploadOutlined />}>Upload</Button>
                      </Upload>
                    </Form.Item>
                  )}
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Insurance Name" name="insurance_name">
                    <Input placeholder="Insurance Name" disabled={viewMode || isViewMode} />
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Insurance Status" name="insurance_status">
                    <Select
                      options={[
                        { value: "Valid", label: "Valid" },
                        { value: "Expired", label: "Expired" },
                      ]}
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  {!isFieldHidden("Insurance Scan Copy") && (
                    <Form.Item
                      label="Insurance Scan Copy"
                      name="insurance_scan"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                    >
                      <Upload
                        beforeUpload={() => false}
                        maxCount={1}
                        accept=".jpg,.jpeg,.png,.pdf"
                        disabled={viewMode || isViewMode}
                      >
                        <Button icon={<UploadOutlined />}>Upload</Button>
                      </Upload>
                    </Form.Item>
                  )}
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item
                    label="Insurance Expiry Date"
                    name="insurance_expiry"
                  >
                    <DatePicker
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                      placeholder="Insurance Expiry Date"
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>)}

                {!isQuick && isAdminAndHrUser && (<Col xs={24} sm={8}>
                  <Form.Item
                    label="Client Labour Contract Expiry Date"
                    name="client_labour_contract_expiry"
                  >
                    <DatePicker
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>)}

                {!isQuick && isAdminAndHrUser && (<Col xs={24} sm={8}>
                  {!isFieldHidden("Client Labour Contract Scan Copy") && (
                    <Form.Item
                      label="Client Labour Contract Scan Copy"
                      name="client_labour_contract_scan"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                    >
                      <Upload
                        beforeUpload={() => false}
                        maxCount={1}
                        accept=".jpg,.jpeg,.png,.pdf"
                        disabled={viewMode || isViewMode}
                      >
                        <Button icon={<UploadOutlined />}>Upload</Button>
                      </Upload>
                    </Form.Item>
                  )}
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Expiry Reminder Time (Days)">
                    <Space>
                      <Form.Item name="first_reminder" noStyle>
                        <InputNumber
                          min={0}
                          placeholder="1st"
                          disabled={viewMode || isViewMode}
                          onWheel={(e) => e.target.blur()}
                        />
                      </Form.Item>
                      <Form.Item name="second_reminder" noStyle>
                        <InputNumber
                          min={0}
                          placeholder="2nd"
                          disabled={viewMode || isViewMode}
                          onWheel={(e) => e.target.blur()}
                        />
                      </Form.Item>
                    </Space>
                  </Form.Item>
                </Col>)}
                {/* Warning Letter */}
                {!isFieldHidden("Warning Letter") && (
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label="Warning Letter"
                      name="warning_letter"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                    >
                      <Upload beforeUpload={() => false} maxCount={1} accept=".jpg,.jpeg,.png,.pdf,.doc,.docx" disabled={viewMode || isViewMode}>
                        <Button icon={<UploadOutlined />}>Upload</Button>
                      </Upload>
                    </Form.Item>
                  </Col>
                )}

                {/* Memo */}
                {!isFieldHidden("Memo") && (
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label="Memo"
                      name="memo_document"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                    >
                      <Upload beforeUpload={() => false} maxCount={1} accept=".jpg,.jpeg,.png,.pdf,.doc,.docx" disabled={viewMode || isViewMode}>
                        <Button icon={<UploadOutlined />}>Upload</Button>
                      </Upload>
                    </Form.Item>
                  </Col>
                )}

                {/* Other Agreements */}
                {!isFieldHidden("Other Agreements") && (
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label="Other Agreements"
                      name="other_agreements_document"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                    >
                      <Upload beforeUpload={() => false} maxCount={1} accept=".jpg,.jpeg,.png,.pdf,.doc,.docx" disabled={viewMode || isViewMode}>
                        <Button icon={<UploadOutlined />}>Upload</Button>
                      </Upload>
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </div>
          </Panel>

          {/* ================= JOB AND POSITION INFORMATION ================= */}
          <Panel
            className="emp-panel-anchor-job"
            header={<PanelHeader panelKey="job" title="Job and Position Information" required categoryKey="job_career" />}
            key="job"
          >
            <div
              style={{
                border: "1px solid #f0f0f0",
                padding: 16,
                borderRadius: 8,
                marginBottom: 16,
                background: "#fafafa",
              }}
            >
              <Row gutter={12}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Date of Joining"
                    name="joining_date"
                    rules={[
                      { required: true, message: "Joining Date  required" },
                    ]}
                  >
                    <DatePicker
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                      placeholder="Date of Joining"
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Select Department"
                    name="department_id"
                    rules={[{ required: true, message: "Department required" }]}
                  >
                    <Select
                      showSearch
                      id="department_id"
                      placeholder="Select Department"
                      popupRender={(menu) => (
                        <>
                          {menu}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              padding: 8,
                              borderTop: "1px solid #f0f0f0",
                            }}
                          >
                            <Button
                              type="link"
                              icon={<PlusOutlined />}
                              onClick={() => setDepartmentModalOpen(true)}
                            >
                              Add New Department
                            </Button>
                          </div>
                        </>
                      )}
                      disabled={viewMode || isViewMode}
                    >
                      {departments?.map((d) => (
                        <Select.Option key={String(d.id)} value={String(d.id)}>
                          {d.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Designation"
                    name="designation_id"
                    rules={[
                      { required: true, message: "Designation required" },
                    ]}
                  >
                    <Select
                      showSearch
                      id="designation_id"
                      placeholder="Select Designation"
                      popupRender={(menu) => (
                        <>
                          {menu}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              padding: 8,
                            }}
                          >
                            <Button
                              type="link"
                              icon={<PlusOutlined />}
                              onClick={() => setDesignationModalOpen(true)}
                            >
                              Add New Designation
                            </Button>
                          </div>
                        </>
                      )}
                      disabled={viewMode || isViewMode}
                    >
                      {designations?.map((d) => (
                        <Select.Option key={String(d.id)} value={String(d.id)}>
                          {d.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                {!isQuick && (<Col xs={24} sm={8}>
                  {!isFieldHidden("Reporting Manager") && (
                    <Form.Item
                      label="Reporting Manager"
                      name="reporting_manager"
                    >
                      <Select
                        showSearch
                        placeholder="Reporting Manager"
                        disabled={viewMode || isViewMode}
                      >
                        value=
                        {reporting_manager
                          ? {
                            value: reporting_manager,
                            label:
                              employees.find(
                                (emp) => emp.employee_id == reporting_manager
                              )?.employee_name +
                              " - " +
                              employees.find(
                                (emp) => emp.employee_id == reporting_manager
                              )?.employee_code,
                          }
                          : null}
                        {employees
                          ?.filter((emp) => emp.employee_id !== employee_id)
                          .map((emp) => (
                            <Select.Option
                              key={String(emp.employee_id)}
                              value={String(emp.employee_id)}
                            >
                              {emp.employee_name} - {emp.employee_code}
                            </Select.Option>
                          ))}
                      </Select>
                    </Form.Item>
                  )}
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Employment Type" name="employment_type">
                    <Radio.Group disabled={viewMode || isViewMode}>
                      <Radio value="ftime">Full-time</Radio>
                      <Radio value="ptime">Part-time</Radio>
                      <Radio value="contract">Contract</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Work Location" name="work_location">
                    <Radio.Group disabled={viewMode || isViewMode}>
                      <Radio value="office">Office</Radio>
                      <Radio value="remote">Remote</Radio>
                      <Radio value="hybrid">Hybrid</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Employee Status" name="employee_status">
                    <Radio.Group disabled={viewMode || isViewMode}>
                      <Radio value="active">Active</Radio>
                      <Radio value="onleave">On Leave</Radio>
                      <Radio value="resigned">Resigned</Radio>
                      <Radio value="terminated">Terminated</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Employee Type" name="employee_type">
                    <Radio.Group disabled={viewMode || isViewMode}>
                      <Radio value="permanent">Permanent</Radio>
                      <Radio value="probationary">Probationary</Radio>
                      <Radio value="temporary">Temporary</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Job Grade / Level" name="job_grade">
                    <Input
                      placeholder="Job Grade / Level"
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Custom Weekend Days">
                    <Checkbox
                      checked={useCustomWeekend}
                      onChange={(e) => setUseCustomWeekend(e.target.checked)}
                      disabled={viewMode || isViewMode}
                    >
                      This employee has different weekend days
                    </Checkbox>
                  </Form.Item>
                </Col>)}

                {!isQuick && useCustomWeekend && (<Col xs={24} sm={16}>
                  <Form.Item
                    label="Weekend Days"
                    tooltip="Overrides the company's weekend setting for this employee only — affects their attendance calendar, leave dates, and payslip working-days count."
                  >
                    <Select
                      mode="multiple"
                      placeholder="Select weekend days"
                      value={customWeekendDays}
                      onChange={setCustomWeekendDays}
                      disabled={viewMode || isViewMode}
                      options={[
                        { label: "Sunday", value: 0 },
                        { label: "Monday", value: 1 },
                        { label: "Tuesday", value: 2 },
                        { label: "Wednesday", value: 3 },
                        { label: "Thursday", value: 4 },
                        { label: "Friday", value: 5 },
                        { label: "Saturday", value: 6 },
                      ]}
                    />
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24}>
                  <Form.Item
                    label="Employee Role / Responsibilities"
                    name="responsibilities"
                  >
                    <TextArea
                      rows={4}
                      placeholder="Employee - Roles & Responsibilities"
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>)}
              </Row>
            </div>
          </Panel>

          {/* ================= COMPENSATION & BENEFITS ================= */}
          {!hideSalaryComponent && !isQuick && (
            <Panel
              className="emp-panel-anchor-salary"
              header={<PanelHeader panelKey="salary" title="Compensation & Benefits" categoryKey="compensation_leave" />}
              key="salary"
            >
              <Row gutter={12} justify="space-between" align="middle">
                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item
                    label="Salary Update Date"
                    name="salary_update_date"
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      placeholder="Salary Update Date"
                      value={
                        salary_update_date ? dayjs(salary_update_date) : null
                      }
                      onChange={(date) =>
                        salary_update_date_set(
                          date ? date.format("YYYY-MM-DD") : null
                        )
                      }
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col
                  xs={24}
                  sm={16}
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    flexWrap: "wrap",
                    gap: 12,
                  }}
                >
                  <Space direction="vertical" align="end">
                    {(editing || isViewMode) && (
                      <Space>
                        <Typography.Text>
                          Download Salary Certificate
                        </Typography.Text>
                        <Tooltip title="Download Employee Salary Certificate">
                          <Button
                            size="small"
                            type="primary"
                            icon={<FileTextOutlined />}
                            onClick={() => downloadSalaryCertificate()}
                            disabled={viewMode || isViewMode}
                          ></Button>
                        </Tooltip>
                      </Space>
                    )}

                    {hasEmployeeHistory && (
                      <Space>
                        <Typography.Text>
                          Download Salary History
                        </Typography.Text>
                        <Tooltip title="Download Entire Salary History Sheet in Excel">
                          <Button
                            size="small"
                            type="primary"
                            icon={<HistoryOutlined />}
                            onClick={() =>
                              downloadExcel("salary", editing?.employee_id)
                            }
                            disabled={viewMode || isViewMode}
                          />
                        </Tooltip>
                      </Space>
                    )}

                    <Space>
                      <Typography.Text>
                        <Tooltip title="Salary details should be hidden from others and only accessible to the admin and accountant">
                          <InfoCircleOutlined
                            style={{ marginRight: 6, color: "#999" }}
                          />
                        </Tooltip>
                        Hide Salary Details
                      </Typography.Text>
                      <Checkbox
                        checked={hideSalaryDetails}
                        onChange={(e) => {
                          setHideSalaryDetails(e.target.checked);
                          hide_salary_details_from_others_set(e.target.checked);
                        }}
                        disabled={viewMode || isViewMode}
                      />
                    </Space>
                  </Space>
                </Col>)}
              </Row>

              <Row
                gutter={12}
                style={{
                  background: "#f5f5f5",
                  borderRadius: 6,
                  padding: "12px 8px",
                  marginTop: 8,
                }}
              >
                {!isQuick && (<Col xs={24} sm={12}>
                  <Form.Item label="Total Salary">
                    <Select
                      placeholder="Select Allowance"
                      value={null}
                      onChange={handleAllowanceSelect}
                      style={{ width: "60%" }}
                      disabled={viewMode || isViewMode}
                    >
                      {ALLOWANCES.filter(
                        (a) => !selectedAllowances.includes(a)
                      ).map((item) => (
                        <Select.Option key={item} value={item}>
                          {item}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={12}>
                  <Typography.Text strong>Salary Calculator</Typography.Text>
                  <Table
                    size="small"
                    style={{ marginTop: 10 }}
                    dataSource={selectedAllowances.map((allowance) => {
                      const key = allowance.replace(/\s+/g, "_");
                      const allowanceObj =
                        allowanceValues.find((item) => item.name === key) || {};

                      return {
                        key,
                        components: allowance,
                        amount:
                          editingAllowance === allowance ? (
                            <Input
                              type="number"
                              value={allowanceObj.amount || ""}
                              autoFocus
                              onChange={(e) =>
                                handleAllowanceValueChange(
                                  allowance,
                                  e.target.value
                                )
                              }
                              onBlur={() => setEditingAllowance(null)}
                              onKeyDown={handleKeyDownOnAllowance}
                              style={{
                                width: "80px",
                                textAlign: "right",
                              }}
                              min={0}
                              max={9999999999}
                              disabled={viewMode || isViewMode}
                            />
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <span>{allowanceObj.amount || "0.00"}</span>
                              {!viewMode && (
                                <Button
                                  type="text"
                                  icon={<EditOutlined />}
                                  size="small"
                                  onClick={() => setEditingAllowance(allowance)}
                                />
                              )}
                            </div>
                          ),
                        action:
                          !isViewMode && !viewMode ? (
                            <Button
                              type="text"
                              icon={<CloseOutlined style={{ color: "red" }} />}
                              size="small"
                              onClick={() => handleAllowanceRemove(allowance)}
                            />
                          ) : (
                            <MinusOutlined />
                          ),
                      };
                    })}
                    columns={[
                      {
                        title: "Salary Components",
                        dataIndex: "components",
                        key: "components",
                      },
                      {
                        title: "Amount",
                        dataIndex: "amount",
                        key: "amount",
                        align: "center",
                      },
                      {
                        title: "Actions",
                        dataIndex: "action",
                        key: "action",
                        align: "right",
                      },
                    ]}
                    pagination={false}
                    footer={() => (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          padding: "10px 16px",
                          background: "#fafafa",
                          fontWeight: "bold",
                          borderTop: "1px solid #f0f0f0",
                        }}
                      >
                        <div style={{ textAlign: "right" }}>
                          <div>
                            Total Monthly Salary:{" "}
                            {Number(totalMonthlySalary || 0).toFixed(2)}
                          </div>
                          <div>
                            Total Annual Salary:{" "}
                            {Number(totalAnnualSalary || 0).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    )}
                  />
                </Col>)}
              </Row>

              <Divider />

              <Collapse ghost bordered={false}>
                <Panel header="Payslip Generator" key="pay">
                  <Suspense fallback={<div>Loading Payslip...</div>}>
                    <PayslipPage
                      employee={editing}
                      authInfo={authInfo}
                      companySettings={companySettings}
                    />
                  </Suspense>
                </Panel>
              </Collapse>
            </Panel>
          )}

          {/* 🔹 Sales & Target Information */}
          {!isQuick && (<Panel
            className="emp-panel-anchor-sales_target"
            header={<PanelHeader panelKey="sales_target" title="Sales & Target Information" />}
            key="sales_target"
          >
            <div
              style={{
                border: "1px solid #f0f0f0",
                padding: 16,
                borderRadius: 8,
                marginBottom: 16,
                background: "#fafafa",
              }}
            >
              <Row gutter={12}>
                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Target Amount" name="target_amount">
                    <InputNumber
                      min={0}
                      step={0.01}
                      style={{ width: "100%" }}
                      placeholder="Target Amount"
                      value={target_amount}
                      onChange={(value) => {
                        const num = parseFloat(value) || 0;
                        if (!isNaN(num)) target_amount_set(num);
                      }}
                      onKeyDown={(e) => {
                        const isNumericKey = /[0-9]/.test(e.key);
                        const isDecimalPoint = e.key === ".";
                        const currentValue = target_amount
                          ? target_amount.toString()
                          : "";
                        const decimalIndex = currentValue.indexOf(".");

                        if (
                          !isNumericKey &&
                          !isDecimalPoint &&
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight"
                        ) {
                          e.preventDefault();
                        }

                        if (isDecimalPoint && currentValue.includes(".")) {
                          e.preventDefault();
                        }

                        if (
                          decimalIndex !== -1 &&
                          currentValue.substring(decimalIndex + 1).length >=
                          2 &&
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight"
                        ) {
                          e.preventDefault();
                        }

                        if (
                          currentValue.length >= 10 &&
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight"
                        ) {
                          e.preventDefault();
                        }
                      }}
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>)}

                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Commission %" name="commission_per">
                    <InputNumber
                      min={0}
                      max={100}
                      step={0.01}
                      style={{ width: "100%" }}
                      onWheel={(e) => e.target.blur()}
                      value={commission_per}
                      placeholder="Commission %"
                      onChange={(value) => {
                        const num = parseFloat(value) || 0;
                        if (!isNaN(num)) commission_per_set(num);
                      }}
                      onKeyDown={(e) => {
                        const isNumericKey = /[0-9]/.test(e.key);
                        const isDecimalPoint = e.key === ".";
                        const currentValue = commission_per
                          ? commission_per.toString()
                          : "";
                        const decimalIndex = currentValue.indexOf(".");

                        if (
                          !isNumericKey &&
                          !isDecimalPoint &&
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight"
                        ) {
                          e.preventDefault();
                        }

                        if (isDecimalPoint && currentValue.includes(".")) {
                          e.preventDefault();
                        }

                        if (
                          decimalIndex !== -1 &&
                          currentValue.substring(decimalIndex + 1).length >=
                          2 &&
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight"
                        ) {
                          e.preventDefault();
                        }

                        if (
                          currentValue.length >= 10 &&
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight"
                        ) {
                          e.preventDefault();
                        }
                      }}
                      disabled={viewMode || isViewMode}
                    />
                  </Form.Item>
                </Col>)}
              </Row>
            </div>
          </Panel>)}

          {/* ================= CLIENT / PROJECT INFORMATION ================= */}
          {!isFieldHidden("Client / Project Information") && !isQuick && (
            <Panel
              className="emp-panel-anchor-client_project"
              header={<PanelHeader panelKey="client_project" title="Client / Project Information" categoryKey="client_other" />}
              key="client_project"
            >
              <div
                style={{
                  border: "1px solid #f0f0f0",
                  padding: 16,
                  borderRadius: 8,
                  marginBottom: 16,
                  background: "#fafafa",
                }}
              >
                <Row gutter={12}>
                  {/* Client Name */}
                  {!isQuick && (<Col xs={24} sm={6}>
                    <Form.Item
                      label={
                        <span>
                          Client Name{" "}
                          <Tooltip title="Client to be registered from Client section">
                            <InfoCircleOutlined
                              style={{ marginLeft: 6, color: "#999" }}
                            />
                          </Tooltip>
                        </span>
                      }
                    >
                      <Select
                        showSearch
                        placeholder="Select or type client name"
                        value={selectedClient?.id || null}
                        onChange={(id) => {
                          if (id === "AddNew") {
                            setClientModalOpen(true)
                            return;
                          }
                          const selectedObj = (clients || []).find(
                            (c) => c.id === id
                          );
                          setSelectedClient(selectedObj || null);
                          setClientReportingManager(null);
                        }}
                        disabled={viewMode || isViewMode}
                        optionFilterProp="children"
                        style={{ width: "100%" }}
                      >
                        <Select.Option key="AddNew" value="AddNew">
                          <PlusOutlined style={{ marginRight: 6 }} /> Add New
                        </Select.Option>
                        {(clients || []).map((c) => (
                          <Select.Option key={c.id} value={c.id}>
                            {c.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>)}

                  {/* Client Reporting Manager */}
                  {!isQuick && (<Col xs={24} sm={6}>
                    <Form.Item
                      label={
                        <span>
                          Client Side Reporting Manager{" "}
                          <Tooltip title="Lists the Client User Registered in Client Section">
                            <InfoCircleOutlined
                              style={{ marginLeft: 6, color: "#999" }}
                            />
                          </Tooltip>
                        </span>
                      }
                    >
                      <Select
                        showSearch
                        placeholder="Client Side Reporting Manager"
                        value={clientReportingManager?.id || null}
                        onChange={(id) => {
                          if (id === "AddNew") {
                            setClientUserModalOpen(true)
                            return;
                          }
                          const selectedObj = (clientReportingManagers || [])
                            .filter((u) => u.client_id === selectedClient?.id)
                            .find((u) => u.id === id);
                          setClientReportingManager(selectedObj || null);
                        }}
                        disabled={!selectedClient || viewMode || isViewMode}
                        optionFilterProp="children"
                        style={{ width: "100%" }}
                      >
                        <Select.Option key="AddNew" value="AddNew">
                          <PlusOutlined style={{ marginRight: 6 }} /> Add New
                        </Select.Option>
                        {(clientReportingManagers || [])
                          .filter((u) => u.client_id === selectedClient?.id)
                          .map((u) => (
                            <Select.Option key={u.id} value={u.id}>
                              {u.name}
                            </Select.Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </Col>)}

                  {/* Enable Timesheet / Attendance Approval */}
                  {!isQuick && (<Col xs={24} sm={4}>
                    <Form.Item label="Enable Timesheet / Attendance Approval by Client?">
                      <Checkbox
                        checked={enable_attendance}
                        onChange={handleAttendanceCheckbox}
                        disabled={
                          !clientReportingManager || viewMode || isViewMode
                        }
                      />
                    </Form.Item>
                  </Col>)}

                  {/* Select Attendance Day */}
                  {(isAttendanceModalOpen || isEdit) && (
                    <Col xs={24} sm={4}>
                      <Form.Item
                        label={
                          <span>
                            Select Day <span className="form-span">*</span>
                          </span>
                        }
                      >
                        <Select
                          value={selected_day}
                          onChange={handleDaySelection}
                          disabled={
                            !enable_attendance || viewMode || isViewMode
                          }
                          placeholder="Select Day"
                        >
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(
                            (day) => (
                              <Select.Option key={day} value={day}>
                                {day}
                              </Select.Option>
                            )
                          )}
                        </Select>
                      </Form.Item>
                    </Col>
                  )}

                  {/* Use Client Leave Policy */}
                  {!isQuick && (<Col xs={24} sm={4}>
                    <Form.Item label="Use Client Leave Policy">
                      <Checkbox
                        checked={use_client_policy}
                        onChange={(e) =>
                          use_client_policy_set(e.target.checked)
                        }
                        disabled={!selectedClient || viewMode || isViewMode}
                      />
                    </Form.Item>
                  </Col>)}

                  {/* Remove Client */}
                  {!isQuick && (<Col xs={24} sm={6}>
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        setSelectedClient(null);
                        setClientReportingManager(null);
                        enable_attendance_set(false);
                        selected_day_set(null);
                        use_client_policy_set(false);
                      }}
                      disabled={viewMode || isViewMode}
                      danger
                      block
                    >
                      Remove Client & Selections
                    </Button>
                  </Col>)}
                </Row>
              </div>
            </Panel>
          )}

          {/* ================= LEAVES INFORMATION ================= */}
          {!isQuick && (<Panel
            className="emp-panel-anchor-leaves_info"
            header={<PanelHeader panelKey="leaves_info" title="Leaves Information" />}
            key="leaves_info"
          >
            <UnmarkedAttendanceAlert
              unmarkedAttendance={leaveSummary?.unmarkedAttendance}
              style={{ marginBottom: 16 }}
            />
            {/* Policy override + total balance strip */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 13, color: "#595959" }}>Max Allowed Leaves to Carry Forward</span>
                <InputNumber
                  min={0}
                  placeholder={
                    use_client_policy
                      ? selectedClient?.leaves_carry_forward ?? 0
                      : companySettings?.leaves_carry_forward ?? 0
                  }
                  value={employeeCarryForwardOverride}
                  onChange={(v) =>
                    setEmployeeCarryForwardOverride(v === null ? null : Number(v))
                  }
                  disabled={viewMode || isViewMode}
                  style={{ width: 100 }}
                />
                <Tooltip title="Leave empty to follow company/client policy">
                  <InfoCircleOutlined style={{ color: "#999" }} />
                </Tooltip>
              </div>

              <div
                style={{
                  background: "#f6ffed",
                  border: "1px solid #b7eb8f",
                  borderRadius: 8,
                  padding: "6px 20px",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 11, color: "#389e0d", fontWeight: 700, letterSpacing: 0.4 }}>
                  TOTAL LEAVE BALANCE
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#237804", lineHeight: 1.3 }}>
                  {totalLeaveBalance}
                </div>
              </div>
            </div>

            <Row gutter={[16, 16]}>
              {/* Annual / Casual Leave */}
              <Col xs={24} md={8}>
                <div style={leaveCardStyle("#1677ff")}>
                  <div style={LEAVE_CARD_HEADER_STYLE}>
                    {authInfo?.country === "India" ? "Casual Leave" : "Annual Leave"}
                  </div>
                  <LeaveStatRow
                    label="Entitlement"
                    value={
                      use_client_policy
                        ? selectedClient?.annual_leave_days ?? 0
                        : companySettings?.annual_leave_days ?? 0
                    }
                  />
                  <LeaveStatRow
                    label="Monthly Accrued"
                    value={(
                      (use_client_policy
                        ? selectedClient?.annual_leave_days
                        : companySettings?.annual_leave_days || 0) / 12
                    ).toFixed(2)}
                  />
                  <LeaveStatRow
                    label="Taken (Previous Year)"
                    value={
                      isEditingPrevYear ? (
                        <InputNumber
                          min={0}
                          size="small"
                          style={{ width: 90 }}
                          value={leavesTakenPrevYear}
                          autoFocus
                          onFocus={() => {
                            if (leavesTakenPrevYear === 0) {
                              setLeavesTakenPrevYear("");
                            }
                          }}
                          onBlur={(e) => {
                            if (e.target.value === "") {
                              setLeavesTakenPrevYear(0);
                            }
                            setIsEditingPrevYear(false);
                          }}
                          onChange={(val) => setLeavesTakenPrevYear(val || 0)}
                          onPressEnter={() => setIsEditingPrevYear(false)}
                          onWheel={(e) => e.target.blur()}
                        />
                      ) : (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {leavesTakenPrevYear}
                          {!isViewMode && !viewMode && (
                            <Button
                              type="text"
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => setIsEditingPrevYear(true)}
                            />
                          )}
                        </span>
                      )
                    }
                  />
                  <LeaveStatRow label="Carry Forward from Last Year" value={carryForward} />
                  <LeaveStatRow
                    label={
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        Taken (Current Year)
                        <Tooltip title="Calculated from approved/pending leave records since the last work anniversary">
                          <InfoCircleOutlined style={{ color: "#999" }} />
                        </Tooltip>
                      </span>
                    }
                    value={leaveSummaryLoading ? <Spin size="small" /> : leavesTakenCurrYear}
                  />
                  <LeaveStatRow label="Balance" value={totalLeaveBalance} strong noBorder />
                </div>
              </Col>

              {/* Sick Leave */}
              <Col xs={24} md={8}>
                <div style={leaveCardStyle("#fa8c16")}>
                  <div style={LEAVE_CARD_HEADER_STYLE}>Sick Leave</div>
                  <LeaveStatRow label="Entitlement" value={leaveSummary?.sickLeave?.quota ?? 0} />
                  <LeaveStatRow
                    label="Taken"
                    value={leaveSummaryLoading ? <Spin size="small" /> : (leaveSummary?.sickLeave?.used ?? 0)}
                  />
                  <LeaveStatRow
                    label="Balance"
                    value={leaveSummaryLoading ? <Spin size="small" /> : (leaveSummary?.sickLeave?.balance ?? 0)}
                    strong
                    noBorder
                  />
                </div>
              </Col>

              {/* Flexi Leave */}
              <Col xs={24} md={8}>
                <div style={leaveCardStyle("#722ed1")}>
                  <div style={LEAVE_CARD_HEADER_STYLE}>Flexi Leave</div>
                  <LeaveStatRow label="Entitlement" value={leaveSummary?.flexiLeave?.quota ?? 0} />
                  <LeaveStatRow
                    label="Taken"
                    value={leaveSummaryLoading ? <Spin size="small" /> : (leaveSummary?.flexiLeave?.used ?? 0)}
                  />
                  <LeaveStatRow
                    label="Balance"
                    value={leaveSummaryLoading ? <Spin size="small" /> : (leaveSummary?.flexiLeave?.balance ?? 0)}
                    strong
                    noBorder
                  />
                </div>
              </Col>
            </Row>

            {!isQuick && editing?.employee_id && (
              <Button
                type="link"
                icon={<HistoryOutlined />}
                style={{ paddingLeft: 0, marginTop: 14 }}
                onClick={() => setLeaveSummaryDrawerOpen(true)}
              >
                View Leave Summary
              </Button>
            )}
            <EmployeeLeaveSummaryDrawer
              open={leaveSummaryDrawerOpen}
              onClose={() => setLeaveSummaryDrawerOpen(false)}
              employeeName={editing?.employee_name}
              leaveSummary={leaveSummary}
              loading={leaveSummaryLoading}
              isIndia={authInfo?.country === "India"}
            />
          </Panel>)}

          {/* ================= GRATUITY ESTIMATOR ================= */}
          {!isQuick && editing?.employee_id && (
            <Panel
              className="emp-panel-anchor-gratuity_estimate"
              header={<PanelHeader panelKey="gratuity_estimate" title="End of Service Gratuity" />}
              key="gratuity_estimate"
            >
              <GratuityEstimatorCard
                employee={editing}
                currency={authInfo?.currency}
                isIndia={authInfo?.country === "India"}
              />
            </Panel>
          )}

          {/* ================= PAYROLL INFORMATION ================= */}
          {!isQuick && (<Panel
            className="emp-panel-anchor-payroll_info"
            header={<PanelHeader panelKey="payroll_info" title="Payroll Information" />}
            key="payroll_info"
          >
            <div
              style={{
                border: "1px solid #f0f0f0",
                padding: 10,
                borderRadius: 8,
                marginBottom: 16,
                background: "#fafafa",
              }}
            >
              <Row gutter={12}>
                {/* Bank Account Number */}
                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Bank Account Number" name="bank_acc_number">
                    <Input
                      placeholder="Bank Account Number"
                      value={bank_acc_number}
                      onChange={(e) => bank_acc_number_set(e.target.value)}
                      disabled={viewMode || isViewMode}
                      onKeyDown={(e) => {
                        const isNumericKey = /[0-9]/.test(e.key);
                        if (
                          !isNumericKey &&
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight"
                        ) {
                          e.preventDefault();
                        }
                        if (
                          bank_acc_number &&
                          bank_acc_number.toString().length >= 20 &&
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight"
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>)}

                {/* Bank Account Name */}
                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Bank Account Name" name="bank_acc_name">
                    <Input
                      placeholder="Bank Account Name"
                      value={bank_acc_name}
                      onChange={(e) => bank_acc_name_set(e.target.value)}
                      disabled={viewMode || isViewMode}
                      onKeyDown={(e) => {
                        if (
                          bank_acc_name &&
                          bank_acc_name.toString().length >= 50 &&
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight"
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>)}

                {/* Bank Name */}
                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Bank Name" name="bank_name">
                    <Input
                      placeholder="Bank Name"
                      value={bank_name}
                      onChange={(e) => bank_name_set(e.target.value)}
                      disabled={viewMode || isViewMode}
                      onKeyDown={(e) => {
                        if (
                          bank_name &&
                          bank_name.toString().length >= 50 &&
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight"
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>)}

                {/* Bank Branch */}
                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="Bank Branch" name="bank_branch">
                    <Input
                      placeholder="Bank Branch"
                      value={bank_branch}
                      onChange={(e) => bank_branch_set(e.target.value)}
                      disabled={viewMode || isViewMode}
                      onKeyDown={(e) => {
                        if (
                          bank_branch &&
                          bank_branch.toString().length >= 50 &&
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight"
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>)}

                {/* IBAN Number */}
                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="IBAN No." name="iban_no">
                    <Input
                      placeholder="IBAN No."
                      value={iban_no}
                      onChange={(e) => iban_no_set(e.target.value)}
                      disabled={viewMode || isViewMode}
                      onKeyDown={(e) => {
                        // 34 is the ISO 13616 maximum IBAN length across all
                        // countries (e.g. Malta/San Marino use the longest
                        // real-world IBANs) — was previously capped at 20,
                        // which truncated most non-Indian employees' IBANs.
                        if (
                          iban_no &&
                          iban_no.toString().length >= 34 &&
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight"
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>)}

                {/* IFSC Code */}
                {!isQuick && (<Col xs={24} sm={8}>
                  <Form.Item label="IFSC Code" name="ifsc_code">
                    <Input
                      placeholder="IFSC Code"
                      value={ifsc_code}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        const regex = /^[A-Z0-9]*$/;
                        if (regex.test(value) || value === "") {
                          ifsc_code_set(value);
                        }
                      }}
                      disabled={viewMode || isViewMode}
                      onKeyDown={(e) => {
                        const isNumber = e.key >= "0" && e.key <= "9";
                        const isLetter =
                          (e.key >= "a" && e.key <= "z") ||
                          (e.key >= "A" && e.key <= "Z");
                        const allowed = [
                          "Backspace",
                          "Delete",
                          "ArrowLeft",
                          "ArrowRight",
                        ];
                        if (
                          !isNumber &&
                          !isLetter &&
                          !allowed.includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                        if (
                          ifsc_code &&
                          ifsc_code.toString().length >= 20 &&
                          !allowed.includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>)}

                {/* PAN Card */}
                {!isQuick && authInfo?.country === "India" && (<Col xs={24} sm={8}>
                  <Form.Item label="PAN Card No." name="pan_card">
                    <Input
                      placeholder="PAN Card No."
                      value={pan_card}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        const regex = /^[A-Z0-9]*$/;
                        if (regex.test(value) || value === "") {
                          pan_card_set(value);
                        }
                      }}
                      disabled={viewMode || isViewMode}
                      onKeyDown={(e) => {
                        const isNumber = e.key >= "0" && e.key <= "9";
                        const isLetter =
                          (e.key >= "a" && e.key <= "z") ||
                          (e.key >= "A" && e.key <= "Z");
                        const allowed = [
                          "Backspace",
                          "Delete",
                          "ArrowLeft",
                          "ArrowRight",
                        ];
                        if (
                          !isNumber &&
                          !isLetter &&
                          !allowed.includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                        if (
                          pan_card &&
                          pan_card.toString().length >= 20 &&
                          !allowed.includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Col>)}
              </Row>
            </div>
          </Panel>)}

          {/* ================= WORK EXPERIENCE ================= */}
          {!isFieldHidden("Work Experience") && !isQuick && (
            <Panel
              className="emp-panel-anchor-work_experience"
              header={<PanelHeader panelKey="work_experience" title="Work Experience" />}
              key="work_experience"
            >
              {experiences?.map((experience, index) => (
                <div
                  key={index}
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: 16,
                    borderRadius: 8,
                    marginBottom: 16,
                    background: "#fafafa",
                  }}
                >
                  <Row gutter={12}>
                    {/* Previous Employer */}
                    {!isQuick && (<Col xs={24} sm={12}>
                      <Form.Item label="Previous Employer">
                        <Input
                          placeholder="Employer Name"
                          value={experience.employer}
                          onChange={(e) =>
                            handleExperienceChange(
                              index,
                              "employer",
                              e.target.value
                            )
                          }
                          onKeyDown={(e) => {
                            if (
                              experience.employer &&
                              experience.employer.toString().length >= 200 &&
                              ![
                                "Backspace",
                                "Delete",
                                "ArrowLeft",
                                "ArrowRight",
                              ].includes(e.key)
                            ) {
                              e.preventDefault();
                            }
                          }}
                          disabled={viewMode || isViewMode}
                        />
                      </Form.Item>
                    </Col>)}

                    {/* Job Title */}
                    {!isQuick && (<Col xs={24} sm={12}>
                      <Form.Item label="Job Title">
                        <Input
                          placeholder="Job Title"
                          value={experience.jobTitle}
                          onChange={(e) =>
                            handleExperienceChange(
                              index,
                              "jobTitle",
                              e.target.value
                            )
                          }
                          onKeyDown={(e) => {
                            if (
                              experience.jobTitle &&
                              experience.jobTitle.toString().length >= 200 &&
                              ![
                                "Backspace",
                                "Delete",
                                "ArrowLeft",
                                "ArrowRight",
                              ].includes(e.key)
                            ) {
                              e.preventDefault();
                            }
                          }}
                          disabled={viewMode || isViewMode}
                        />
                      </Form.Item>
                    </Col>)}

                    {/* Start Date */}
                    {!isQuick && (<Col xs={24} sm={6}>
                      <Form.Item label="Start Date">
                        <DatePicker
                          style={{ width: "100%" }}
                          value={
                            experience.startDate
                              ? dayjs(experience.startDate)
                              : null
                          }
                          onChange={(date) =>
                            handleExperienceChange(index, "startDate", date)
                          }
                          disabled={viewMode || isViewMode}
                          format="YYYY-MM-DD"
                        />
                      </Form.Item>
                    </Col>)}

                    {/* End Date */}
                    {!isQuick && (<Col xs={24} sm={6}>
                      <Form.Item label="End Date">
                        <DatePicker
                          style={{ width: "100%" }}
                          value={
                            experience.endDate
                              ? dayjs(experience.endDate)
                              : null
                          }
                          onChange={(date) =>
                            handleExperienceChange(index, "endDate", date)
                          }
                          disabled={viewMode || isViewMode}
                          format="YYYY-MM-DD"
                        />
                      </Form.Item>
                    </Col>)}

                    {/* Job Responsibilities */}
                    {!isQuick && (<Col xs={24}>
                      <Form.Item label="Job Responsibilities">
                        <TextArea
                          placeholder="Describe responsibilities..."
                          autoSize={{ minRows: 4, maxRows: 6 }}
                          value={experience.responsibilities}
                          onChange={(e) =>
                            handleExperienceChange(
                              index,
                              "responsibilities",
                              e.target.value
                            )
                          }
                          disabled={viewMode || isViewMode}
                        />
                      </Form.Item>
                    </Col>)}

                    {/* Professional Achievements */}
                    {!isQuick && (<Col xs={24}>
                      <Form.Item label="Professional Achievements">
                        <TextArea
                          placeholder="List achievements..."
                          autoSize={{ minRows: 3, maxRows: 6 }}
                          value={experience.achievements}
                          onChange={(e) =>
                            handleExperienceChange(
                              index,
                              "achievements",
                              e.target.value
                            )
                          }
                          disabled={viewMode || isViewMode}
                        />
                      </Form.Item>
                    </Col>)}

                    {/* Remove Experience Button */}
                    {!isViewMode && !viewMode && !isQuick && (
                      <Col xs={24} sm={6} style={{ marginTop: 8 }}>
                        {index > 0 && (
                          <Button
                            danger
                            type="default"
                            onClick={() => removeExperience(index)}
                          >
                            Remove Experience
                          </Button>
                        )}
                      </Col>
                    )}
                  </Row>
                </div>
              ))}

              {/* Add New Experience Button */}
              {!isViewMode && !viewMode && (
                <Button type="primary" onClick={addExperience}>
                  Add Experience
                </Button>
              )}
            </Panel>
          )}

          {!isFieldHidden("Performance Management") && !isQuick && (
            <Panel
              className="emp-panel-anchor-performance_management"
              header={<PanelHeader panelKey="performance_management" title="Performance Management" />}
              key="performance_management"
            >
              <div
                style={{
                  border: "1px solid #f0f0f0",
                  padding: 16,
                  borderRadius: 8,
                  marginBottom: 16,
                  background: "#fafafa",
                }}
              >
                <Collapse accordion={false} defaultActiveKey={["review_0"]}>
                  {performance_reviews.map((entry, index) => (
                    <Panel
                      key={`review_${index}`}
                      header={
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                          <Space>
                            <Typography.Text strong style={{ color: "#1890ff" }}>
                              Review #{index + 1}
                            </Typography.Text>
                            {entry.date ? (
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {dayjs(entry.date).format("DD MMM YYYY")}
                              </Typography.Text>
                            ) : (
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                No date selected
                              </Typography.Text>
                            )}
                          </Space>
                          {index > 0 && !viewMode && !isViewMode && (
                            <Button
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={(e) => {
                                e.stopPropagation(); // prevent collapse toggle
                                removePerformanceReview(index);
                              }}
                              style={{ marginLeft: "auto" }}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      }
                    >
                      <Row gutter={[12, 12]}>
                        <Col xs={24} sm={8}>
                          <Form.Item label="Review Date" style={{ marginBottom: 8 }}>
                            <DatePicker
                              style={{ width: "100%" }}
                              value={entry.date ? dayjs(entry.date) : null}
                              onChange={(date) =>
                                handlePerformanceReviewChange(
                                  index,
                                  "date",
                                  date ? date.format("YYYY-MM-DD") : null
                                )
                              }
                              format="DD/MM/YYYY"
                              disabled={viewMode || isViewMode}
                              placeholder="Select review date"
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={[12, 12]}>
                        <Col xs={24} sm={18}>
                          <Form.Item label="Performance Review">
                            <TextArea
                              placeholder="Performance Reviews"
                              value={entry.review}
                              onChange={(e) =>
                                handlePerformanceReviewChange(index, "review", e.target.value)
                              }
                              autoSize={{ minRows: 3 }}
                              disabled={viewMode || isViewMode}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={18}>
                          <Form.Item label="Goals / Objectives">
                            <TextArea
                              placeholder="Goals/Objectives"
                              value={entry.goals}
                              onChange={(e) =>
                                handlePerformanceReviewChange(index, "goals", e.target.value)
                              }
                              autoSize={{ minRows: 3 }}
                              disabled={viewMode || isViewMode}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={18}>
                          <Form.Item label="Feedback for Improvements">
                            <TextArea
                              placeholder="Feedback for Improvements"
                              value={entry.feedback}
                              onChange={(e) =>
                                handlePerformanceReviewChange(index, "feedback", e.target.value)
                              }
                              autoSize={{ minRows: 3 }}
                              disabled={viewMode || isViewMode}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={18}>
                          <Form.Item label="Training and Development Needs">
                            <TextArea
                              placeholder="Training and Development Needs"
                              value={entry.training}
                              onChange={(e) =>
                                handlePerformanceReviewChange(index, "training", e.target.value)
                              }
                              autoSize={{ minRows: 3, maxRows: 6 }}
                              disabled={viewMode || isViewMode}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={18}>
                          <Form.Item label="Promotions and Career Path">
                            <TextArea
                              placeholder="Promotions and Career Path"
                              value={entry.promotion}
                              onChange={(e) =>
                                handlePerformanceReviewChange(index, "promotion", e.target.value)
                              }
                              autoSize={{ minRows: 3, maxRows: 6 }}
                              disabled={viewMode || isViewMode}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Panel>
                  ))}
                </Collapse>

                {!viewMode && !isViewMode && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={addPerformanceReview}
                    style={{ marginTop: 12 }}
                  >
                    Add Performance Review
                  </Button>
                )}
              </div>
            </Panel>
          )}

          {/* ================= DISCIPLINARY AND LEGAL RECORDS ================= */}
          {!isFieldHidden("Disciplinary and Legal Records") && !isQuick && (
            <Panel
              className="emp-panel-anchor-disciplinary_legal"
              header={<PanelHeader panelKey="disciplinary_legal" title="Disciplinary and Legal Records" categoryKey="compliance_conduct" />}
              key="disciplinary_legal"
            >
              <div
                style={{
                  border: "1px solid #f0f0f0",
                  padding: 16,
                  borderRadius: 8,
                  marginBottom: 16,
                  background: "#fafafa",
                }}
              >
                <Collapse accordion={false} defaultActiveKey={["disciplinary_0"]}>
                  {disciplinaryRecords.map((entry, index) => (
                    <Panel
                      key={`disciplinary_${index}`}
                      header={
                        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                          <Space>
                            <Typography.Text strong style={{ color: "#ff4d4f" }}>
                              Record #{index + 1}
                            </Typography.Text>
                            {entry.date ? (
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {dayjs(entry.date).format("DD MMM YYYY")}
                              </Typography.Text>
                            ) : (
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                No date selected
                              </Typography.Text>
                            )}
                            {entry.actionTaken && (
                              <Typography.Text
                                style={{
                                  fontSize: 11,
                                  background: "#fff1f0",
                                  color: "#cf1322",
                                  padding: "1px 8px",
                                  borderRadius: 4,
                                  border: "1px solid #ffa39e",
                                }}
                              >
                                {entry.actionTaken}
                              </Typography.Text>
                            )}
                          </Space>
                          {index > 0 && !viewMode && !isViewMode && (
                            <Button
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeDisciplinaryRecord(index);
                              }}
                              style={{ marginLeft: "auto" }}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      }
                    >
                      <Row gutter={[12, 12]}>
                        {/* Date */}
                        <Col xs={24} sm={8}>
                          <Form.Item label="Record Date">
                            <DatePicker
                              style={{ width: "100%" }}
                              value={entry.date ? dayjs(entry.date) : null}
                              onChange={(date) =>
                                handleDisciplinaryChange(
                                  index, "date",
                                  date ? date.format("YYYY-MM-DD") : null
                                )
                              }
                              format="DD/MM/YYYY"
                              disabled={viewMode || isViewMode}
                              placeholder="Select date"
                            />
                          </Form.Item>
                        </Col>

                        {/* Action Taken */}
                        <Col xs={24} sm={8}>
                          <Form.Item label="Action Taken">
                            <Radio.Group
                              value={entry.actionTaken}
                              onChange={(e) =>
                                handleDisciplinaryChange(index, "actionTaken", e.target.value)
                              }
                              disabled={viewMode || isViewMode}
                            >
                              <Radio value="warning">Warning</Radio>
                              <Radio value="suspension">Suspension</Radio>
                              <Radio value="termination">Termination</Radio>
                              <Radio value="other">Other</Radio>
                            </Radio.Group>
                          </Form.Item>
                        </Col>

                        {/* Acknowledged */}
                        <Col xs={24} sm={8}>
                          <Form.Item label="Acknowledgment">
                            <Checkbox
                              checked={entry.acknowledged}
                              onChange={(e) =>
                                handleDisciplinaryChange(index, "acknowledged", e.target.checked)
                              }
                              disabled={viewMode || isViewMode}
                            >
                              Acknowledged by Employee
                            </Checkbox>
                          </Form.Item>
                        </Col>

                        {/* Disciplinary Actions */}
                        <Col xs={24} sm={12}>
                          <Form.Item label="Disciplinary Actions">
                            <TextArea
                              placeholder="Enter disciplinary actions (Warnings, Suspensions, etc.)"
                              value={entry.disciplinaryActions}
                              onChange={(e) =>
                                handleDisciplinaryChange(index, "disciplinaryActions", e.target.value)
                              }
                              autoSize={{ minRows: 3 }}
                              disabled={viewMode || isViewMode}
                            />
                          </Form.Item>
                        </Col>

                        {/* Legal Documents */}
                        <Col xs={24} sm={12}>
                          <Form.Item label="Legal Documents">
                            <TextArea
                              placeholder="Enter Legal Documents (Contracts, Agreements, NDAs)"
                              value={entry.legalDocuments}
                              onChange={(e) =>
                                handleDisciplinaryChange(index, "legalDocuments", e.target.value)
                              }
                              autoSize={{ minRows: 3 }}
                              disabled={viewMode || isViewMode}
                            />
                          </Form.Item>
                        </Col>

                        {/* Contract Details */}
                        <Col xs={24} sm={12}>
                          <Form.Item label="Contract Details">
                            <TextArea
                              placeholder="Enter Contract Details"
                              value={entry.contractDetails}
                              onChange={(e) =>
                                handleDisciplinaryChange(index, "contractDetails", e.target.value)
                              }
                              autoSize={{ minRows: 3 }}
                              disabled={viewMode || isViewMode}
                            />
                          </Form.Item>
                        </Col>

                        {/* Document Upload */}
                        <Col xs={24} sm={12}>
                          <Form.Item label="Attach Document">
                            {entry.documentPath && !entry.documentFile && (
                              <div style={{ marginBottom: 8 }}>
                                <a href={entry.documentPath} target="_blank" rel="noreferrer">
                                  View Existing Document
                                </a>
                              </div>
                            )}
                            <Upload
                              beforeUpload={(file) => {
                                handleDisciplinaryChange(index, "documentFile", file);
                                return false;
                              }}
                              onRemove={() =>
                                handleDisciplinaryChange(index, "documentFile", null)
                              }
                              maxCount={1}
                              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                              disabled={viewMode || isViewMode}
                              fileList={
                                entry.documentFile
                                  ? [
                                    {
                                      uid: `disc_${index}`,
                                      name: entry.documentFile.name,
                                      status: "done",
                                    },
                                  ]
                                  : []
                              }
                            >
                              {!viewMode && !isViewMode && (
                                <Button icon={<UploadOutlined />}>Upload Document</Button>
                              )}
                            </Upload>
                          </Form.Item>
                        </Col>
                      </Row>
                    </Panel>
                  ))}
                </Collapse>

                {!viewMode && !isViewMode && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={addDisciplinaryRecord}
                    style={{ marginTop: 12 }}
                  >
                    Add Record
                  </Button>
                )}
              </div>
            </Panel>
          )}

          {/* ================= TRAINING AND DEVELOPMENT ================= */}
          {!isFieldHidden("Training and Development") && !isQuick && (
            <Panel
              className="emp-panel-anchor-training_development"
              header={<PanelHeader panelKey="training_development" title="Training and Development" />}
              key="training_development"
            >
              <div
                style={{
                  border: "1px solid #f0f0f0",
                  padding: 16,
                  borderRadius: 8,
                  marginBottom: 16,
                  background: "#fafafa",
                }}
              >
                <Row gutter={[12, 12]}>
                  {/* Completed Training Courses */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Completed Training Courses">
                      <TextArea
                        placeholder="List completed courses..."
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        value={trainingData.completedCourses}
                        onChange={(e) =>
                          setTrainingData({
                            ...trainingData,
                            completedCourses: e.target.value,
                          })
                        }
                        disabled={viewMode || isViewMode}
                      />
                    </Form.Item>
                  </Col>)}

                  {/* Certifications */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Certifications">
                      <TextArea
                        placeholder="List certifications..."
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        value={trainingData.certifications}
                        onChange={(e) =>
                          setTrainingData({
                            ...trainingData,
                            certifications: e.target.value,
                          })
                        }
                        disabled={viewMode || isViewMode}
                      />
                    </Form.Item>
                  </Col>)}

                  {/* Upcoming Training Plans */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Upcoming Training Plans">
                      <TextArea
                        placeholder="List upcoming trainings..."
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        value={trainingData.upcomingTrainings}
                        onChange={(e) =>
                          setTrainingData({
                            ...trainingData,
                            upcomingTrainings: e.target.value,
                          })
                        }
                        disabled={viewMode || isViewMode}
                      />
                    </Form.Item>
                  </Col>)}

                  {/* Skills Development Programs */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Skills Development Programs">
                      <TextArea
                        placeholder="List skills development programs..."
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        value={trainingData.skillsDevelopment}
                        onChange={(e) =>
                          setTrainingData({
                            ...trainingData,
                            skillsDevelopment: e.target.value,
                          })
                        }
                        disabled={viewMode || isViewMode}
                      />
                    </Form.Item>
                  </Col>)}

                  {/* Professional Development Initiatives */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Professional Development Initiatives">
                      <TextArea
                        placeholder="List professional development initiatives..."
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        value={trainingData.professionalDevelopment}
                        onChange={(e) =>
                          setTrainingData({
                            ...trainingData,
                            professionalDevelopment: e.target.value,
                          })
                        }
                        disabled={viewMode || isViewMode}
                      />
                    </Form.Item>
                  </Col>)}

                  {/* Preferred Training Mode */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Preferred Training Mode">
                      <Checkbox.Group
                        disabled={viewMode || isViewMode}
                        value={trainingData.trainingModes
                          .map((checked, idx) => (checked ? idx : null))
                          .filter((v) => v !== null)}
                        onChange={(checkedValues) => {
                          const updatedModes = [false, false, false];
                          checkedValues.forEach((idx) => {
                            updatedModes[idx] = true;
                          });
                          setTrainingData({
                            ...trainingData,
                            trainingModes: updatedModes,
                          });
                        }}
                      >
                        <Row>
                          {!isQuick && (<Col span={12}>
                            <Checkbox value={0}>Online</Checkbox>
                          </Col>)}
                          {!isQuick && (<Col span={12}>
                            <Checkbox value={1}>In-Person</Checkbox>
                          </Col>)}
                          {!isQuick && (<Col span={12}>
                            <Checkbox value={2}>Hybrid</Checkbox>
                          </Col>)}
                        </Row>
                      </Checkbox.Group>
                    </Form.Item>
                  </Col>)}

                  {/* Interest in Future Training */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Interested in Future Training?">
                      <Checkbox
                        checked={trainingData.futureTrainingInterest}
                        onChange={(e) =>
                          setTrainingData({
                            ...trainingData,
                            futureTrainingInterest: e.target.checked,
                          })
                        }
                        disabled={viewMode || isViewMode}
                      >
                        Yes
                      </Checkbox>
                    </Form.Item>
                  </Col>)}
                </Row>
              </div>
            </Panel>
          )}

          {/* ================= HEALTH AND SAFETY INFORMATION ================= */}
          {!isFieldHidden("Health and Safety Information") && !isQuick && (
            <Panel
              className="emp-panel-anchor-health_safety_info"
              header={<PanelHeader panelKey="health_safety_info" title="Health and Safety Information" />}
              key="health_safety_info"
            >
              <div
                style={{
                  border: "1px solid #f0f0f0",
                  padding: 16,
                  borderRadius: 8,
                  marginBottom: 16,
                  background: "#fafafa",
                }}
              >
                <Row gutter={[12, 12]}>
                  {/* Medical Records */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Medical Records (if required by company policy)">
                      <TextArea
                        placeholder="Enter medical records details here"
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        value={medical_records}
                        onChange={(e) => medical_records_set(e.target.value)}
                        disabled={viewMode || isViewMode}
                      />
                    </Form.Item>
                  </Col>)}

                  {/* Worker's Compensation */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Worker's Compensation">
                      <TextArea
                        placeholder="Enter worker's compensation details here"
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        value={workers_compensation}
                        onChange={(e) =>
                          workers_compensation_set(e.target.value)
                        }
                        disabled={viewMode || isViewMode}
                      />
                    </Form.Item>
                  </Col>)}

                  {/* Occupational Health Assessments */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Occupational Health Assessments">
                      <TextArea
                        placeholder="Enter health assessments details here"
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        value={health_assessments}
                        onChange={(e) => health_assessments_set(e.target.value)}
                        disabled={viewMode || isViewMode}
                      />
                    </Form.Item>
                  </Col>)}

                  {/* First Aid / Health Plans */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="First Aid / Health Plans">
                      <TextArea
                        placeholder="Enter first aid or health plan details here"
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        value={health_plans}
                        onChange={(e) => health_plans_set(e.target.value)}
                        disabled={viewMode || isViewMode}
                      />
                    </Form.Item>
                  </Col>)}

                  {/* Health Assessment Completed */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Health Assessment Completed?">
                      <Radio.Group
                        value={health_assessment_completed}
                        onChange={handleHealthAssessmentRadio}
                        disabled={viewMode || isViewMode}
                      >
                        <Radio value="yes">Yes</Radio>
                        <Radio value="no">No</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>)}

                  {/* First Aid Training Completed */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="First Aid Training Completed">
                      <Checkbox
                        checked={first_aid_training}
                        onChange={(e) =>
                          first_aid_training_set(e.target.checked)
                        }
                        disabled={viewMode || isViewMode}
                      >
                        Yes
                      </Checkbox>
                    </Form.Item>
                  </Col>)}
                </Row>
              </div>
            </Panel>
          )}

          {/* ================= OTHER INFORMATION ================= */}
          {!isFieldHidden("Other Information") && !isQuick && (
            <Panel
              className="emp-panel-anchor-other_information"
              header={<PanelHeader panelKey="other_information" title="Other Information" />}
              key="other_information"
            >
              <div
                style={{
                  border: "1px solid #f0f0f0",
                  padding: 16,
                  borderRadius: 8,
                  marginBottom: 16,
                  background: "#fafafa",
                }}
              >
                <Row gutter={[12, 12]}>
                  {/* Add Company Asset */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Add Company Asset">
                      <Input
                        placeholder="E.g., Laptop, Phone"
                        value={newAsset}
                        onChange={(e) => setNewAsset(e.target.value)}
                        onPressEnter={addAsset}
                        disabled={viewMode || isViewMode}
                        allowClear
                      />
                      {!isViewMode && (
                        <Button
                          onClick={addAsset}
                          type="default"
                          icon={<PlusOutlined />}
                          style={{ marginTop: 8 }}
                        >
                          Add Asset
                        </Button>
                      )}
                    </Form.Item>
                  </Col>)}

                  {/* Assigned Assets (Multi Select) */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Assigned Assets">
                      <Select
                        mode="multiple"
                        style={{ width: "100%" }}
                        placeholder="Select or add company assets"
                        value={
                          Array.isArray(companyAssets) ? companyAssets : []
                        }
                        disabled={viewMode || isViewMode}
                        onChange={(selectedAssets) =>
                          setCompanyAssets(selectedAssets)
                        }
                        options={(companyAssets || []).map((item) => ({
                          value: item,
                          label: item,
                        }))}
                        onSearch={(inputValue) => {
                          if (
                            inputValue &&
                            !companyAssets.includes(inputValue) &&
                            !isViewMode
                          ) {
                            setCompanyAssets([...companyAssets, inputValue]);
                          }
                        }}
                        tagRender={(props) => {
                          const { label, closable, onClose } = props;
                          return (
                            <Space
                              style={{
                                display: "flex",
                                alignItems: "center",
                                background: "#f5f5f5",
                                padding: "2px 8px",
                                borderRadius: "4px",
                              }}
                            >
                              {label}
                              {closable && (
                                <CloseOutlined
                                  onClick={onClose}
                                  style={{
                                    marginLeft: 5,
                                    fontSize: 12,
                                    cursor: "pointer",
                                    color: "#999",
                                  }}
                                />
                              )}
                            </Space>
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>)}

                  {/* HR Documents */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="HR Documents">
                      <Checkbox.Group
                        disabled={viewMode || isViewMode}
                        value={[
                          signedPolicies && "signedPolicies",
                          confidentialityAgreements &&
                          "confidentialityAgreements",
                        ].filter(Boolean)}
                        onChange={(checkedValues) => {
                          setSignedPolicies(
                            checkedValues.includes("signedPolicies")
                          );
                          setConfidentialityAgreements(
                            checkedValues.includes("confidentialityAgreements")
                          );
                        }}
                      >
                        <Row>
                          {!isQuick && (<Col span={24}>
                            <Checkbox value="signedPolicies">
                              Signed Policies
                            </Checkbox>
                          </Col>)}
                          {!isQuick && (<Col span={24}>
                            <Checkbox value="confidentialityAgreements">
                              Confidentiality Agreements
                            </Checkbox>
                          </Col>)}
                        </Row>
                      </Checkbox.Group>
                    </Form.Item>
                  </Col>)}

                  {/* Social Media Links */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="LinkedIn Profile">
                      <Input
                        placeholder="LinkedIn Profile URL"
                        value={linkedin}
                        onChange={(e) => setLinkedin(e.target.value)}
                        maxLength={120}
                        disabled={viewMode || isViewMode}
                      />
                    </Form.Item>
                  </Col>)}

                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Facebook Profile">
                      <Input
                        placeholder="Facebook Profile URL"
                        value={facebook}
                        onChange={(e) => setFacebook(e.target.value)}
                        maxLength={120}
                        disabled={viewMode || isViewMode}
                      />
                    </Form.Item>
                  </Col>)}

                  {/* Work-related Projects */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Work-related Projects">
                      <TextArea
                        placeholder="Describe projects or contributions..."
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        value={workProjects}
                        onChange={(e) => setWorkProjects(e.target.value)}
                        disabled={viewMode || isViewMode}
                      />
                    </Form.Item>
                  </Col>)}

                  {/* Optional: Upload Supporting Documents */}

                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item
                      label="Other Documents"
                      name="other_documents"
                      valuePropName="fileList"
                      getValueFromEvent={(e) => {
                        if (Array.isArray(e)) return e;
                        return e?.fileList || [];
                      }}
                      rules={[
                        {
                          validator: (_, value) =>
                            value && value.length > 5
                              ? Promise.reject("Maximum 5 documents allowed")
                              : Promise.resolve(),
                        },
                      ]}
                    >
                      <Upload
                        beforeUpload={() => false}
                        multiple
                        maxCount={5}
                        disabled={viewMode || isViewMode}
                      >
                        {!viewMode && !isViewMode && (
                          <Button icon={<UploadOutlined />}>
                            Upload Documents (Max 5)
                          </Button>
                        )}
                      </Upload>
                    </Form.Item>
                  </Col>)}

                </Row>
              </div>
            </Panel>
          )}

          {/* ================= EXIT / TERMINATION INFORMATION ================= */}
          {!isFieldHidden("Exit / Termination Information") && !isQuick && isAdminAndHrUser && (
            <Panel
              className="emp-panel-anchor-exit_termination_info"
              header={<PanelHeader panelKey="exit_termination_info" title="Exit / Termination Information" categoryKey="exit_offboarding" />}
              key="exit_termination_info"
            >
              <div
                style={{
                  border: "1px solid #f0f0f0",
                  padding: 16,
                  borderRadius: 8,
                  marginBottom: 16,
                  background: "#fafafa",
                }}
              >

                <Row gutter={[12, 12]}>
                  {/* Contract Completion Date */}
                  {!isQuick && (<Col xs={24} sm={24}>
                    <Checkbox
                      checked={isContractCompleted}
                      onChange={(e) => setIsContractCompleted(e.target.checked)}
                      disabled={viewMode || isViewMode}
                    >
                      Contract Completed
                    </Checkbox>
                    {isContractCompleted && <Form.Item label="Contract Completion Date" style={{ marginTop: 8 }}>
                      <DatePicker
                        style={{ width: !screens.md ? "100%" : "50%" }}
                        value={contractCompletionDate}
                        onChange={(d) => setContractCompletionDate(d || dayjs())}
                        format="DD/MM/YYYY"
                        disabled={viewMode || isViewMode}
                      />
                    </Form.Item>}

                    {/* Final Settlement Collapse */}
                    <Collapse ghost bordered={false}>

                      <Panel
                        header={
                          <Space>
                            Final Settlement
                            {!isContractCompleted && (
                              <Tooltip title="Please check 'Contract Completed' to enable final settlement details.">
                                <InfoCircleOutlined style={{ color: "#fa8c16" }} />
                              </Tooltip>
                            )}
                          </Space>
                        }
                        key="settle"
                        // This disables the reveal button if the checkbox is not checked
                        collapsible={!isContractCompleted ? "disabled" : "header"}
                      >
                        <div
                          style={{
                            border: "1px solid #e6e6e6",
                            borderRadius: 10,
                            background: "#fff",
                            padding: 20,
                          }}
                        >
                          <Space align="center" style={{ marginBottom: 16 }}>
                            <Typography.Text strong>
                              Contract Completion Date:
                            </Typography.Text>

                            <DatePicker
                              value={contractCompletionDate}
                              onChange={(d) => setContractCompletionDate(d || dayjs())}
                              format="DD/MM/YYYY"
                              disabled={viewMode || isViewMode}
                            />
                          </Space>

                          <Suspense fallback={<div>Loading Final Settlement...</div>}>
                            <FinalSettlementForm
                              employee={editing}
                              authInfo={authInfo}
                              contractCompletionDate={contractCompletionDate}
                            />
                          </Suspense>
                        </div>
                      </Panel>
                    </Collapse>
                  </Col>)}

                  {/* Reason for Leaving */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Reason for Leaving">
                      <Radio.Group
                        value={reason_for_leaving}
                        onChange={(e) => reason_for_leaving_set(e.target.value)}
                        disabled={viewMode || isViewMode}
                      >
                        <Radio value="resignation">Resignation</Radio>
                        <Radio value="retirement">Retirement</Radio>
                        <Radio value="termination">Termination</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>)}


                  {!isQuick && (<Col xs={24} sm={8}>
                    {!isFieldHidden("Labour Cancellation Copy") && (
                      <Form.Item label="Labour Cancellation Copy"
                        name="labour_cancellation_copy"
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                      >
                        <Upload beforeUpload={() => false}
                          maxCount={1}
                          accept=".jpg,.jpeg,.png,.pdf"
                          disabled={viewMode || isViewMode}
                        >
                          <Button icon={<UploadOutlined />}>Upload</Button>
                        </Upload>
                      </Form.Item>)}
                    {!isFieldHidden("Visa Cancellation Copy") && (
                      <Form.Item label="Visa Cancellation Copy"
                        name="visa_cancellation_copy"
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                      >
                        <Upload beforeUpload={() => false}
                          maxCount={1}
                          accept=".jpg,.jpeg,.png,.pdf"
                          disabled={viewMode || isViewMode}
                        >
                          <Button icon={<UploadOutlined />}>Upload</Button>
                        </Upload>
                      </Form.Item>)}
                    {!isFieldHidden("Experience Letter") && (
                      <Form.Item label="Experience Letter"
                        name="experience_letter"
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                      >
                        <Upload beforeUpload={() => false}
                          maxCount={1}
                          accept=".jpg,.jpeg,.png,.pdf"
                          disabled={viewMode || isViewMode}
                        >
                          <Button icon={<UploadOutlined />}>Upload</Button>
                        </Upload>
                      </Form.Item>)}
                    {!isFieldHidden("Insurance Cancellation Copy") && (
                      <Form.Item label="Insurance Cancellation Copy"
                        name="insurance_cancellation_copy"
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                      >
                        <Upload beforeUpload={() => false}
                          maxCount={1}
                          accept=".jpg,.jpeg,.png,.pdf"
                          disabled={viewMode || isViewMode}
                        >
                          <Button icon={<UploadOutlined />}>Upload</Button>
                        </Upload>
                      </Form.Item>)}
                    {!isFieldHidden("Insurance Cancelled Refund") && (
                      <Form.Item label="Insurance Cancelled Refund"
                        name="insurance_cancelled_refund"
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                      >
                        <Upload beforeUpload={() => false}
                          maxCount={1}
                          accept=".jpg,.jpeg,.png,.pdf"
                          disabled={viewMode || isViewMode}
                        >
                          <Button icon={<UploadOutlined />}>Upload</Button>
                        </Upload>
                      </Form.Item>
                    )}
                    {!isFieldHidden("Signed Final Settlement Copy") && (
                      <Form.Item label="Signed Final Settlement Copy"
                        name="signed_final_settlement_copy"
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                      >
                        <Upload beforeUpload={() => false}
                          maxCount={1}
                          accept=".jpg,.jpeg,.png,.pdf"
                          disabled={viewMode || isViewMode}
                        >
                          <Button icon={<UploadOutlined />}>Upload</Button>
                        </Upload>
                      </Form.Item>
                    )}
                  </Col>)}



                  {/* Exit Interview Details */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Exit Interview Details">
                      <TextArea
                        placeholder="Enter Exit Interview Details"
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        value={exit_interview}
                        onChange={(e) => exit_interview_set(e.target.value)}
                        disabled={viewMode || isViewMode}
                      />
                    </Form.Item>
                  </Col>)}

                  {/* Final Settlement Details */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Final Settlement Details">
                      <TextArea
                        placeholder="Severance Pay, Benefits, etc."
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        value={final_settlement}
                        onChange={(e) => final_settlement_set(e.target.value)}
                        disabled={viewMode || isViewMode}
                      />
                    </Form.Item>
                  </Col>)}

                  {/* Mark Assets as Returned */}
                  {!isQuick && (<Col xs={24} sm={12}>
                    <Form.Item label="Mark Assets as Returned">
                      {companyAssets.length > 0 ? (
                        <Checkbox.Group
                          disabled={viewMode || isViewMode}
                          value={Object.keys(returnedAssets).filter(
                            (asset) => returnedAssets[asset]
                          )}
                          onChange={(selectedValues) => {
                            const updated = {};
                            companyAssets.forEach(
                              (asset) =>
                              (updated[asset] =
                                selectedValues.includes(asset))
                            );
                            setReturnedAssets(updated);
                          }}
                        >
                          <Row gutter={[0, 4]}>
                            {companyAssets.map((asset, index) => (
                              <Col span={24} key={index}>
                                <Checkbox value={asset}>{asset}</Checkbox>
                              </Col>
                            ))}
                          </Row>
                        </Checkbox.Group>
                      ) : (
                        <Text type="secondary">
                          No assets assigned to this employee.
                        </Text>
                      )}
                    </Form.Item>
                  </Col>)}

                </Row>
              </div>
            </Panel>
          )}

          {/* ================= APPLICATION SETTINGS (USER MAPPING & LOGIN) ================= */}
          {!isUserMapped && !editing && (
            <Panel
              className="emp-panel-anchor-application_settings_user_mapping"
              header={<PanelHeader panelKey="application_settings_user_mapping" title="Application Settings for User Mapping and Login" />}
              key="application_settings_user_mapping"
            >
              <div
                style={{
                  border: "1px solid #f0f0f0",
                  padding: 16,
                  borderRadius: 8,
                  marginBottom: 16,
                  background: "#fafafa",
                }}
              >
                <Row
                  gutter={[12, 12]}
                  align="middle"
                  className="employee_mapping_login"
                  style={{ marginTop: 8 }}
                >
                  {/* Map User Checkbox */}

                  <Col xs={24} sm={6}>
                    <Form.Item>
                      <Checkbox
                        checked={is_map_login}
                        onChange={(e) => is_map_login_set(e.target.checked)}
                        disabled={viewMode || isViewMode}
                      >
                        <span style={{ display: "flex", alignItems: "center" }}>
                          Map User
                          <Tooltip
                            title={
                              <>
                                Associate this employee with an existing user account to share login access, roles, and permissions.
                                <br />
                                This is required only if a user has been created earlier; otherwise, it can be left unselected.
                              </>
                            }
                            placement="right"
                          >
                            <InfoCircleOutlined
                              style={{
                                fontSize: 14,
                                color: "#1890ff",
                                marginLeft: 6,
                                cursor: "pointer",
                              }}
                            />
                          </Tooltip>
                        </span>
                      </Checkbox>
                    </Form.Item>
                  </Col>


                  {/* If Map User is enabled and not in View mode */}
                  {is_map_login && !isViewMode && (
                    <Col xs={24} sm={8}>
                      <Form.Item label={<>Select User</>} required>
                        <Select
                          placeholder="Choose a User"
                          style={{ width: "100%" }}
                          data-testid="select-user"
                          value={user_id !== 0 ? user_id : undefined}
                          options={mapEmployeeUsers}
                          onChange={(value, option) => {
                            user_id_set(value);
                            selected_user_set(option);
                            user_map_modal_set(true);
                          }}
                          disabled={viewMode || isViewMode}
                          showSearch
                          optionFilterProp="label"
                        />
                      </Form.Item>
                    </Col>
                  )
                    // : 
                    // (
                    //   // Enable Login Checkbox
                    //   <Col xs={24} sm={6}>
                    //     <Form.Item>
                    //       <Checkbox
                    //         id="enable_login"
                    //         checked={is_login_modal}
                    //         onChange={() => is_login_modal_set(true)}
                    //         disabled={viewMode || isViewMode}
                    //       >
                    //         <span
                    //           style={{ display: "flex", alignItems: "center" }}
                    //         >
                    //           Enable Login
                    //           <Tooltip
                    //             title="Enable login access for this employee"
                    //             placement="right"
                    //           >
                    //             <InfoCircleOutlined
                    //               style={{
                    //                 fontSize: 14,
                    //                 color: "#1890ff",
                    //                 marginLeft: 6,
                    //                 cursor: "pointer",
                    //               }}
                    //             />
                    //           </Tooltip>
                    //         </span>
                    //       </Checkbox>
                    //     </Form.Item>
                    //   </Col>
                    // )
                  }
                </Row>
              </div>
            </Panel>
          )}

          {/* Documents */}
          {/* <Panel header="Documents" key="docs">
            <Upload.Dragger {...uploadProps} style={{ width: "100%" }}>
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Click or drag files to upload</p>
              <p className="ant-upload-hint">Supported: photos, pdf, docs, etc.</p>
            </Upload.Dragger>
          </Panel> */}
        </Collapse>

        {/* ================= BOTTOM ACTION BUTTONS ================= */}


        <Row justify="end" gutter={[12, 12]} style={{ marginTop: 24 }}>
          {!isQuick && (
            <Col>
              <Button type="default" onClick={onCancel} disabled={saving}>
                Discard
              </Button>
            </Col>
          )}

          {!isQuick && (
            <Col>
              <Button
                type="primary"
                loading={saving}
                data-testid="employee-save"
                onClick={() => {
                  const userName = authInfo?.userInfo?.name || "User";
                  if (isEmployeeTrialLimitReached) {
                    Swal.fire({
                      title: "Free Limit Reached",
                      text: `Hi ${userName}, you are currently in Free mode. Only 1 employee is allowed in Free Version.`,
                      icon: "warning",
                    });
                    return;
                  }

                  (!editing && !isUserMapped) ? addLogIn() : handleSave();

                }}
                disabled={viewMode || isViewMode}
              >
                Save
              </Button>
            </Col>
          )}
        </Row>



      </Form>

      {/* ================= ENABLE LOGIN MODAL ================= */}
      <Modal
        title="Enable Login Access"
        open={is_login_modal}
        onCancel={() => is_login_modal_set(false)}
        onOk={addLogIn}
        okText="Save"
        okButtonProps={{ loading: saving }}
        destroyOnHidden
      >
        <Row gutter={[12, 12]}>
          {/* Choose a Branch */}
          <Col xs={24} sm={24}>
            <Form.Item
              label={
                <>
                  Choose a Branch <span style={{ color: "red" }}>*</span>
                </>
              }
            >
              <Select
                showSearch
                placeholder="Choose a Branch"
                style={{ width: "100%" }}
                popupMatchSelectWidth={false}
                value={formValues.selectedBranch?.id}
                onChange={(branchId) => {
                  const branchObj = branches.find((b) => b.id === branchId);
                  formSetValues({
                    ...formValues,
                    selectedBranch: {
                      id: branchObj?.id,
                      name: branchObj?.name,
                    },
                  });
                }}
                options={branches.map((b) => ({ label: b.name, value: b.id }))}
                disabled={viewMode || isViewMode}
              />
            </Form.Item>
          </Col>

          {/* Choose a User Role */}
          <Col xs={24} sm={24}>
            <Form.Item
              label={
                <>
                  Choose a User Role <span style={{ color: "red" }}>*</span>
                </>
              }
            >
              <Select
                placeholder="Choose a User Role"
                style={{ width: "100%" }}
                popupMatchSelectWidth={false}
                value={formValues.selectedUserRole?.value}
                options={filteredUserRoles}
                onChange={(value, option) =>
                  formSetValues({
                    ...formValues,
                    selectedUserRole: { value, label: option.display },
                  })
                }
                disabled={viewMode || isViewMode}
              />
            </Form.Item>
          </Col>

          {/* ============ SEND EMAIL SECTION (From Old UI) ============ */}
          <Col
            xs={24}
            sm={12}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              paddingTop: "1rem",
            }}
          >
            <input
              type="checkbox"
              checked={is_email_sent}
              id="is_email_sent"
              onChange={(e) => is_email_sent_set(e.target.checked)}
              disabled={viewMode || isViewMode}
            />
            <label className="form-label-small" htmlFor="is_email_sent">
              <span style={{ display: "flex", alignItems: "center" }}>
                Send Email
                <Tooltip title="Send Email To Set Password">
                  <img
                    style={{
                      height: "0.9rem",
                      paddingLeft: "5px",
                      cursor: "pointer",
                    }}
                    src={infoPng}
                    alt="info"
                  />
                </Tooltip>
              </span>
            </label>
          </Col>
        </Row>
      </Modal>

      {/* ================= USER MAPPING CONFIRMATION MODAL ================= */}
      <Modal
        open={user_map_modal}
        onCancel={() => {
          user_id_set(0);
          user_map_modal_set(false);
          is_map_login_set(false);
          selected_user_set(undefined);
        }}
        onOk={handleUserMapping}
        okText="Confirm"
        destroyOnHidden
      >
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          <Typography.Title level={4} style={{ marginBottom: 8 }}>
            Employee Email ID Will Be Changed
          </Typography.Title>
          <Typography.Text>
            The email ID will be changed to <b>{selected_user?.email}</b>
            <br />
            Employee Username: <b>{selected_user?.label}</b>
          </Typography.Text>
        </div>
      </Modal>

      {/* Department Modal for add new button */}
      <DepartmentFormModal
        open={departmentModalOpen}
        onClose={() => setDepartmentModalOpen(false)}
        onSuccess={async (createdName) => {
          // 🔁 refresh departments
          const res = await dispatch(fetchDepartments()).unwrap();

          // 🎯 find exact match by name
          const match = res.find(
            (d) => d.name?.toLowerCase() === createdName.toLowerCase()
          );

          if (match?.id) {
            form.setFieldsValue({
              department_id: String(match.id),
            });
          }
        }}
      />


      {/* Designation Modal for add new designation button */}
      <DesignationFormModal
        open={designationModalOpen}
        onClose={() => setDesignationModalOpen(false)}
        onSuccess={async (createdName) => {
          // 🔁 Reload designations
          const res = await dispatch(fetchDesignations()).unwrap();

          // 🎯 find exact match by name
          const match = res.find(
            (d) => d.name?.toLowerCase() === createdName.toLowerCase()
          );

          if (match?.id) {
            form.setFieldsValue({
              designation_id: String(match.id),
            });
          }
        }}
      />


      {/* Client modal for add new client button */}
      <ClientFormModal
        open={clientModalOpen}
        onClose={() => setClientModalOpen(false)}
        authInfo={authInfo}
        onSuccess={async (createdName) => {
          if (!createdName) return;

          // 🔁 Reload clients
          const res = await dispatch(fetchClients()).unwrap();

          const match = res.find(
            (c) =>
              c.name &&
              c.name.toLowerCase() === createdName.toLowerCase()
          );

          if (match) {
            setSelectedClient(match);
            setClientReportingManager(null);
          }
        }}
      />


      {/* Client reporting manager modal open on clicking add new button */}
      <ClientUserModal
        open={clientUserModalOpen}
        onClose={() => setClientUserModalOpen(false)}
        authInfo={authInfo}

        // 🔒 IMPORTANT
        fixedClientId={selectedClient?.id}
        disableClientSelect
        hideClientAddNew

        onSuccess={(createdUser) => {
          if (!createdUser) return;

          dispatch(fetchClientUsers()).then(({ payload }) => {
            const manager = payload.find(
              (u) =>
                u.client_id == selectedClient.id &&
                u.name === createdUser.name
            );

            if (manager) {
              setClientReportingManager(manager);

              // 🔒 RE-ASSERT client
              setSelectedClient(
                clients.find(c => c.id == selectedClient.id)
              );

              // Optional: sync form (only if you rely on it elsewhere)
              form.setFieldsValue({
                client_id: selectedClient.id,
                client_reporting_manager_id: manager.id,
              });
            }
          });
        }}
      />


      {/* ================= LOADING OVERLAY ================= */}
      {saving && (
        <div className="overlay">
          <Loading size="large" />
        </div>
      )}
    </div>
  );
};

export default EmployeeForm;
